import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { FiCalendar, FiList, FiUsers, FiSettings, FiLogOut, FiPlus, FiEdit, FiTrash2, FiFileText, FiGrid, FiDownload, FiUpload } from 'react-icons/fi';
import './App.css';

// Use empty string so requests go through proxy to backend
const API_URL = '';

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Login Component
function Login({ setUser }) {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (username, password) => {
    setError('');
    setLoading(true);

    try {
      console.log('Attempting login with:', { username, backend: API_URL });
      
      const formDataObj = new URLSearchParams();
      formDataObj.append('username', username);
      formDataObj.append('password', password);

      const response = await axios.post(`${API_URL}/api/auth/login`, formDataObj, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      console.log('Login successful:', response.data);
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      
      // Redirect based on role
      if (response.data.user.role === 'lector') {
        navigate('/lector/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      let errorMessage = 'Chyba při přihlášení';
      
      if (err.code === 'ERR_NETWORK') {
        errorMessage = `Nelze se připojit k serveru. Zkontrolujte, zda backend běží na ${API_URL}`;
      } else if (err.response) {
        errorMessage = err.response.data?.detail || 'Nesprávné přihlašovací údaje';
      } else {
        errorMessage = err.message || 'Neznámá chyba';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleLogin(formData.username, formData.password);
  };

  const handleQuickLogin = async (username, password) => {
    setFormData({ username, password });
    await handleLogin(username, password);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Workshop Management</h1>
          <p className="text-gray-600 mt-2">Přihlaste se do svého účtu</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">Chyba:</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Uživatelské jméno
            </label>
            <input
              type="text"
              data-testid="username-input"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Heslo
            </label>
            <input
              type="password"
              data-testid="password-input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loading}
              required
            />
          </div>

          <button
            type="submit"
            data-testid="submit-button"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Přihlašuji...' : 'Přihlásit se'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Dashboard Layout
function DashboardLayout({ user, setUser, children }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-bold text-primary-600">Workshop Management</h1>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {user?.role === 'admin' && (
                  <Link
                    to="/admin-dashboard"
                    data-testid="admin-dashboard-link"
                    className="border-transparent text-gray-500 hover:border-primary-500 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                  >
                    <FiFileText className="mr-2" />
                    Dashboard
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  data-testid="dashboard-link"
                  className="border-transparent text-gray-500 hover:border-primary-500 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  <FiList className="mr-2" />
                  Workshopy
                </Link>
                <Link
                  to="/ai-trainings"
                  data-testid="ai-trainings-link"
                  className="border-transparent text-gray-500 hover:border-primary-500 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  <FiGrid className="mr-2" />
                  Školení AI
                </Link>
                <Link
                  to="/calendar"
                  data-testid="calendar-link"
                  className="border-transparent text-gray-500 hover:border-primary-500 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  <FiCalendar className="mr-2" />
                  Kalendář
                </Link>
                <Link
                  to="/schools"
                  data-testid="schools-link"
                  className="border-transparent text-gray-500 hover:border-primary-500 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  <FiUsers className="mr-2" />
                  Školy
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/settings"
                    data-testid="settings-link"
                    className="border-transparent text-gray-500 hover:border-primary-500 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                  >
                    <FiSettings className="mr-2" />
                    Nastavení
                  </Link>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <span className="text-gray-700 mr-4">Uživatel: {user?.username}</span>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <FiLogOut className="mr-2" />
                Odhlásit
              </button>
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
}


// User Dashboard Component (for regular users)
function UserDashboard() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadUpcomingEvents();
  }, []);

  const loadUpcomingEvents = async () => {
    try {
      setLoading(true);
      const [workshopsRes, trainingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/workshops`),
        axios.get(`${API_URL}/api/ai-trainings`)
      ]);

      const now = new Date();
      
      // Combine and filter upcoming events
      const workshops = workshopsRes.data
        .filter(w => new Date(w.datum) >= now)
        .map(w => ({ ...w, type: 'workshop' }));
      
      const trainings = trainingsRes.data
        .filter(t => new Date(t.datum) >= now)
        .map(t => ({ ...t, type: 'training' }));

      const combined = [...workshops, ...trainings]
        .sort((a, b) => new Date(a.datum) - new Date(b.datum))
        .slice(0, 5);

      setUpcomingEvents(combined);
    } catch (error) {
      console.error('Error loading upcoming events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'd. MMMM yyyy', { locale: cs });
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Přehled</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Workshopy</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">Spravovat</p>
              </div>
              <FiList className="text-4xl text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/ai-trainings')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">AI Školení</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">Spravovat</p>
              </div>
              <FiGrid className="text-4xl text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/calendar')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Kalendář</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">Zobrazit</p>
              </div>
              <FiCalendar className="text-4xl text-purple-500" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Nejbližší termíny</h2>
          {loading ? (
            <p className="text-gray-500">Načítám...</p>
          ) : upcomingEvents.length === 0 ? (
            <p className="text-gray-500">Žádné nadcházející akce</p>
          ) : (
            <div className="space-y-3">
              {upcomingEvents.map((event, index) => (
                <div 
                  key={`${event.type}-${event.id}`} 
                  className="border-l-4 border-primary-500 bg-gray-50 p-4 hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => {
                    if (event.type === 'workshop') {
                      navigate('/dashboard');
                    } else {
                      navigate('/ai-trainings');
                    }
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          event.type === 'workshop' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {event.type === 'workshop' ? 'Workshop' : 'AI Školení'}
                        </span>
                        <h3 className="font-semibold text-gray-800">{event.nazev}</h3>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        <FiCalendar className="inline mr-1" />
                        {formatDate(event.datum)}
                      </p>
                      {event.misto && (
                        <p className="text-sm text-gray-500 mt-1">{event.misto}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Admin Dashboard Component
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [salesStats, setSalesStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
    loadSalesStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSalesStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/sales-stats`);
      setSalesStats(response.data);
    } catch (error) {
      console.error('Error loading sales stats:', error);
    }
  };

  const handleOpenInvoiceInVyfakturuj = async (workshop) => {
    if (!workshop.invoice_number) {
      alert('Pro tento workshop ještě nebyla vytvořena faktura.');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${workshop.id}/vyfakturuj-url`);
      window.open(response.data.vyfakturuj_url, '_blank');
    } catch (error) {
      alert('Chyba při otevírání faktury: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Načítání statistik...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center text-red-600 p-4">
        Nepodařilo se načíst statistiky dashboardu.
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Administrátorský Dashboard</h1>
        <p className="mt-2 text-sm text-gray-700">
          Přehled prodaných workshopů, výnosů a statistik
        </p>
      </div>

      {/* Základní statistiky */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiList className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Celkem workshopů
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.total_workshops}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiFileText className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Celkový výnos
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.total_revenue.toLocaleString()} Kč
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiCalendar className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Realizované
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.realized_workshops} WS
                  </dd>
                  <dd className="text-sm text-green-600">
                    {stats.realized_revenue.toLocaleString()} Kč
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiCalendar className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Naplánované
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.planned_workshops} WS
                  </dd>
                  <dd className="text-sm text-yellow-600">
                    {stats.planned_revenue.toLocaleString()} Kč
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiCalendar className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Tento měsíc
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.current_month_workshops} workshopů
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiFileText className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Výnos tento měsíc
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.current_month_revenue.toLocaleString()} Kč
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grafy a tabulky */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Měsíční statistiky */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Měsíční přehled
            </h3>
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Měsíc
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Workshopy
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Výnos (Kč)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.monthly_stats.slice(-6).map((monthStat) => (
                    <tr key={monthStat.month}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {monthStat.month_display}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {monthStat.workshops}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {monthStat.revenue.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Top programy */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Nejpopulárnější programy
            </h3>
            <div className="space-y-3">
              {stats.top_programs.slice(0, 5).map((program, index) => (
                <div key={program.program_id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                      index === 0 ? 'bg-yellow-500' :
                      index === 1 ? 'bg-gray-400' :
                      index === 2 ? 'bg-yellow-600' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {program.program_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {program.workshops} workshopů
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {program.revenue.toLocaleString()} Kč
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Poslední workshopy */}
      <div className="mt-8 bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Poslední workshopy
          </h3>
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Škola
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workshopy
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cena
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Faktura
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.recent_workshops.map((workshop) => (
                  <tr key={workshop.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {workshop.nazev_skoly}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {workshop.den}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {workshop.total_workshops}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {workshop.cena.toLocaleString()} Kč
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {workshop.invoice_number ? (
                        <button
                          onClick={() => handleOpenInvoiceInVyfakturuj(workshop)}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer transition-colors"
                          title="Otevřít fakturu ve vyfakturuj.cz"
                        >
                          {workshop.invoice_number}
                        </button>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Bez faktury
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Prodejní statistiky podle prodejce */}
      {salesStats && (
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              Prodeje podle prodejce
            </h3>
            <div className="grid gap-6">
              {salesStats.sales_by_seller.map((seller, index) => (
                <div key={seller.seller_name} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-md font-medium text-gray-900">
                      #{index + 1} {seller.seller_name}
                    </h4>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Celkem</div>
                      <div className="text-lg font-bold text-gray-900">
                        {seller.total_workshops} WS • {seller.total_revenue.toLocaleString()} Kč
                      </div>
                    </div>
                  </div>
                  
                  {seller.monthly_stats.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Měsíc</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Workshopy</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Výnos</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {seller.monthly_stats.map((monthStat) => (
                            <tr key={monthStat.month}>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {monthStat.month_display}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-600">
                                {monthStat.workshops} WS
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-600">
                                {monthStat.revenue.toLocaleString()} Kč
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Schools Management Component
function SchoolsManagement() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [formData, setFormData] = useState({
    nazev_skoly: '',
    ico: '',
    osoba: '',
    email: '',
    telefon: '',
    mesto: '',
    ulice: '',
    psc: '',
    okres: '',
    poznamka: ''
  });

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schools`);
      setSchools(response.data);
    } catch (error) {
      console.error('Error loading schools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchool) {
        await axios.put(`${API_URL}/api/schools/${editingSchool.id}`, formData);
        alert('Škola byla úspěšně aktualizována!');
      } else {
        await axios.post(`${API_URL}/api/schools`, formData);
        alert('Škola byla úspěšně přidána!');
      }
      
      setShowForm(false);
      setEditingSchool(null);
      setFormData({
        nazev_skoly: '',
        ico: '',
        osoba: '',
        email: '',
        telefon: '',
        mesto: '',
        ulice: '',
        psc: '',
        okres: '',
        poznamka: ''
      });
      loadSchools();
    } catch (error) {
      alert('Chyba při ukládání školy: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    setFormData({
      nazev_skoly: school.nazev_skoly,
      ico: school.ico,
      osoba: school.osoba,
      email: school.email,
      telefon: school.telefon,
      mesto: school.mesto,
      ulice: school.ulice,
      psc: school.psc,
      okres: school.okres,
      poznamka: school.poznamka || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (school) => {
    if (!window.confirm(`Opravdu chcete smazat školu "${school.nazev_skoly}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/schools/${school.id}`);
      alert('Škola byla úspěšně smazána!');
      loadSchools();
    } catch (error) {
      alert('Chyba při mazání školy: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSchool(null);
    setFormData({
      nazev_skoly: '',
      ico: '',
      osoba: '',
      email: '',
      telefon: '',
      mesto: '',
      ulice: '',
      psc: '',
      okres: '',
      poznamka: ''
    });
  };

  if (loading) {
    return <div className="text-center py-10">Načítání škol...</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Správa škol</h1>
          <p className="mt-2 text-sm text-gray-700">
            Celkový počet škol: {schools.length}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
          >
            <FiPlus className="mr-2" />
            Přidat školu
          </button>
        </div>
      </div>

      {/* Form pro přidání/editaci školy */}
      {showForm && (
        <div className="mt-8 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              {editingSchool ? 'Upravit školu' : 'Přidat novou školu'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Název školy *</label>
                  <input
                    type="text"
                    value={formData.nazev_skoly}
                    onChange={(e) => setFormData({ ...formData, nazev_skoly: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">IČO</label>
                  <input
                    type="text"
                    value={formData.ico}
                    onChange={(e) => setFormData({ ...formData, ico: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Kontaktní osoba</label>
                  <input
                    type="text"
                    value={formData.osoba}
                    onChange={(e) => setFormData({ ...formData, osoba: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Telefon</label>
                  <input
                    type="tel"
                    value={formData.telefon}
                    onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Město</label>
                  <input
                    type="text"
                    value={formData.mesto}
                    onChange={(e) => setFormData({ ...formData, mesto: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ulice</label>
                  <input
                    type="text"
                    value={formData.ulice}
                    onChange={(e) => setFormData({ ...formData, ulice: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">PSČ</label>
                  <input
                    type="text"
                    value={formData.psc}
                    onChange={(e) => setFormData({ ...formData, psc: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Okres</label>
                  <input
                    type="text"
                    value={formData.okres}
                    onChange={(e) => setFormData({ ...formData, okres: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Poznámka</label>
                <textarea
                  value={formData.poznamka}
                  onChange={(e) => setFormData({ ...formData, poznamka: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Zrušit
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  {editingSchool ? 'Aktualizovat školu' : 'Přidat školu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Seznam škol */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Název školy</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Město</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">IČO</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Kontakt</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Workshopy</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Akce</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {schools.map((school) => (
                    <tr key={school.id}>
                      <td className="px-3 py-4 text-sm text-gray-900">
                        <div className="font-medium">{school.nazev_skoly}</div>
                        <div className="text-xs text-gray-500">{school.ulice}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {school.mesto}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {school.ico || '-'}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        <div className="text-sm">{school.osoba}</div>
                        <div className="text-xs text-gray-400">{school.email}</div>
                        <div className="text-xs text-gray-400">{school.telefon}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <div className="text-center">
                          <div className="font-medium">{school.total_workshops || 0}</div>
                          <div className="text-xs text-gray-400">
                            {school.last_workshop ? `Poslední: ${school.last_workshop}` : 'Žádný'}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(school)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Upravit"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(school)}
                            className="text-red-600 hover:text-red-900"
                            title="Smazat"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Workshops List Component
function WorkshopsList() {
  const [workshops, setWorkshops] = useState([]);
  const [lectors, setLectors] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('future'); // all, future, past
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [filterType]); // Reload when filter changes

  const loadData = async () => {
    setLoading(true);
    try {
      const [workshopsRes, lectorsRes, programsRes, statusesRes, gradesRes, sellersRes] = await Promise.all([
        axios.get(`${API_URL}/api/workshops?filter_type=${filterType}`),
        axios.get(`${API_URL}/api/lectors`),
        axios.get(`${API_URL}/api/programs`),
        axios.get(`${API_URL}/api/statuses`),
        axios.get(`${API_URL}/api/grades`),
        axios.get(`${API_URL}/api/sellers`)
      ]);

      setWorkshops(workshopsRes.data);
      setLectors(lectorsRes.data);
      setPrograms(programsRes.data);
      setStatuses(statusesRes.data);
      setGrades(gradesRes.data);
      setSellers(sellersRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Opravdu chcete smazat tento workshop?')) {
      try {
        await axios.delete(`${API_URL}/api/workshops/${id}`);
        loadData();
      } catch (error) {
        alert('Chyba při mazání workshopu');
      }
    }
  };

  const handleCreateInvoice = async (workshop, sendEmail = false) => {
    if (!workshop.ico) {
      alert('Workshop nemá vyplněné IČO. Nejdříve upravte workshop a přidejte IČO školy.');
      return;
    }
    
    try {
      const invoiceData = {
        workshop_id: workshop.id,
        customer_name: workshop.nazev_skoly,
        customer_email: workshop.email,
        customer_street: workshop.ulice,
        customer_city: workshop.mesto,
        customer_zip: workshop.psc,
        customer_ico: workshop.ico,
        amount: workshop.total_price || workshop.cena || 0, // Use total_price if available
        description: `Workshop ${workshop.nazev_skoly} - ${workshop.den}`,
        send_email: sendEmail
      };

      const response = await axios.post(`${API_URL}/api/invoices/create`, invoiceData);
      alert(response.data.message);
      
      // Reload workshops to show updated invoice status
      loadData();
    } catch (error) {
      console.error('Invoice creation error:', error);
      let errorMessage = 'Chyba při vytváření faktury';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          errorMessage += ': ' + (error.response.data.detail || JSON.stringify(error.response.data));
        } else {
          errorMessage += ': ' + error.response.data;
        }
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }
      
      alert(errorMessage);
    }
  };
  
  const handleSendInvoiceEmail = async (workshop) => {
    if (!workshop.invoice_id) {
      alert('Pro tento workshop ještě nebyla vytvořena faktura.');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/invoices/send-email/${workshop.id}`);
      alert(response.data.message);
    } catch (error) {
      alert('Chyba při odesílání emailu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleOpenInvoiceInVyfakturuj = async (workshop) => {
    if (!workshop.invoice_id) {
      alert('Pro tento workshop ještě nebyla vytvořena faktura.');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${workshop.id}/vyfakturuj-url`);
      window.open(response.data.vyfakturuj_url, '_blank');
    } catch (error) {
      alert('Chyba při otevírání faktury: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getNames = (ids, items) => {
    if (!ids || ids.length === 0) return 'N/A';
    return ids.map(id => {
      const item = items.find(i => i.id === id);
      return item ? (item.jmeno || item.nazev) : '';
    }).filter(Boolean).join(', ');
  };

  if (loading) {
    return <div className="text-center py-10">Načítání...</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900" data-testid="workshops-title">
            Seznam workshopů 
            {filterType === 'future' && <span className="text-lg text-blue-600">- Budoucí</span>}
            {filterType === 'past' && <span className="text-lg text-orange-600">- Minulé</span>}
            {filterType === 'all' && <span className="text-lg text-gray-600">- Všechny</span>}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Celkový počet workshopů: {workshops.length}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-3 flex items-center">
          {/* Date Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Zobrazit:</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="future">Budoucí workshopy</option>
              <option value="all">Všechny workshopy</option>
              <option value="past">Minulé workshopy</option>
            </select>
          </div>
          
          <button
            onClick={() => navigate('/workshop/new')}
            data-testid="add-workshop-button"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
          >
            <FiPlus className="mr-2" />
            Přidat workshop
          </button>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300" data-testid="workshops-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Den</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Škola</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Město</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Program</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">WS</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Prodejce</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Lektoři</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Cena</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Faktura</th>
                    <th className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Akce</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {workshops.map((workshop) => (
                    <tr key={workshop.id} data-testid={`workshop-row-${workshop.id}`}>
                      <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-900">
                        {workshop.den}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-900 max-w-xs">
                        <div className="truncate" title={workshop.nazev_skoly}>
                          {workshop.nazev_skoly}
                        </div>
                        <div className="text-xs text-gray-500">
                          {workshop.cely_den === 'ANO' ? 'Celý den' : `${workshop.od || ''} - ${workshop.do || ''}`}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500">
                        {workshop.mesto}
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        <div className="truncate max-w-xs" title={getNames(workshop.program, programs)}>
                          {getNames(workshop.program, programs)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {workshop.total_workshops || 1}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {sellers.find(s => s.id === workshop.kdo_prodal)?.jmeno || 'N/A'}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-sm text-gray-500">
                        <div className="flex flex-wrap gap-1 max-w-24">
                          {workshop.prirazeni_lektoru && workshop.prirazeni_lektoru.length > 0 ? 
                            workshop.prirazeni_lektoru.slice(0, 2).map((lectorId) => {
                              const lector = lectors.find(l => l.id === lectorId);
                              return lector ? (
                                <span key={lectorId} className="inline-flex items-center px-1 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  {lector.jmeno}
                                </span>
                              ) : null;
                            }) : (
                              <span className="text-xs text-gray-400">-</span>
                            )
                          }
                          {workshop.prirazeni_lektoru && workshop.prirazeni_lektoru.length > 2 && (
                            <span className="text-xs text-gray-400">+{workshop.prirazeni_lektoru.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500">
                        <div className="text-right">
                          <div className="font-medium">{workshop.total_price || workshop.cena || 0} Kč</div>
                          <div className="text-xs text-gray-400">celkem</div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {workshop.invoice_id ? (
                          <div className="space-y-1">
                            <button
                              onClick={() => handleOpenInvoiceInVyfakturuj(workshop)}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer transition-colors"
                              title="Otevřít fakturu ve vyfakturuj.cz"
                            >
                              {workshop.invoice_number}
                            </button>
                            {workshop.invoice_paid ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Zaplaceno
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Nezaplaceno
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Bez faktury
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <div className="flex space-x-2 items-center">
                          <button
                            onClick={() => navigate(`/workshop/edit/${workshop.id}`)}
                            data-testid={`edit-button-${workshop.id}`}
                            className="text-primary-600 hover:text-primary-900"
                            title="Upravit"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(workshop.id)}
                            data-testid={`delete-button-${workshop.id}`}
                            className="text-red-600 hover:text-red-900"
                            title="Smazat"
                          >
                            <FiTrash2 />
                          </button>
                          
                          {!workshop.invoice_id ? (
                            <>
                              <button
                                onClick={() => handleCreateInvoice(workshop, false)}
                                data-testid={`invoice-button-${workshop.id}`}
                                className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-600 rounded whitespace-nowrap"
                                title="Vytvořit fakturu"
                              >
                                Vytvořit fakturu
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="flex flex-col space-y-1">
                                <span className="text-xs text-gray-600 font-medium">
                                  Faktura: {workshop.invoice_number}
                                </span>
                                <button
                                  onClick={() => handleSendInvoiceEmail(workshop)}
                                  data-testid={`send-email-button-${workshop.id}`}
                                  className="text-blue-600 hover:text-blue-900 text-xs px-2 py-1 border border-blue-600 rounded whitespace-nowrap"
                                  title="Poslat fakturu emailem"
                                >
                                  Poslat emailem
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Workshop Edit Form Component
function WorkshopEditForm() {
  const navigate = useNavigate();
  const { id } = require('react-router-dom').useParams();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    den: '',
    od: '',
    do: '',
    cely_den: 'NE',
    pocet: 1,
    nazev_skoly: '',
    ico: '',
    osoba: '',
    email: '',
    telefon: '',
    mesto: '',
    ulice: '',
    psc: '',
    okres: '',
    vzdalenost_od_prerova: 0,
    program: [],
    stupen: [],
    stav: '',
    poznamka: '',
    kdo_prodal: '',
    company_id: '',  // Pro výběr fakturační firmy
    prirazeni_lektoru: [],
    gps_latitude: null,
    gps_longitude: null,
    cena: 0,
    pocet_km: 0,
    cena_za_km: 0,
    grouped_with: 'NE',
    workshop_items: [], // Nová pole pro podporu množství workshopů
    total_workshops: 1,
    travel_info: {
      pocet_km: 0,
      cena_za_km: 0,
      popis: '',
      celkova_cena_dopravy: 0
    },
    total_price: 0
  });

  const [lectors, setLectors] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [workshopRes, lectorsRes, programsRes, statusesRes, gradesRes, sellersRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/workshops/${id}`),
        axios.get(`${API_URL}/api/lectors`),
        axios.get(`${API_URL}/api/programs`),
        axios.get(`${API_URL}/api/statuses`),
        axios.get(`${API_URL}/api/grades`),
        axios.get(`${API_URL}/api/sellers`),
        axios.get(`${API_URL}/api/companies`)
      ]);

      setFormData(workshopRes.data);
      setLectors(lectorsRes.data);
      setPrograms(programsRes.data);
      setStatuses(statusesRes.data);
      setGrades(gradesRes.data);
      setSellers(sellersRes.data);
      setCompanies(companiesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Chyba při načítání dat');
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/workshops/${id}`, formData);
      alert('Workshop byl úspěšně aktualizován!');
      navigate('/dashboard');
    } catch (error) {
      alert('Chyba při aktualizaci workshopu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  if (loading) {
    return <div className="text-center py-10">Načítání...</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Upravit workshop
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        {/* Same form fields as WorkshopForm - copy all the fields */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Den *</label>
            <input
              type="date"
              value={formData.den}
              onChange={(e) => setFormData({ ...formData, den: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Od</label>
            <input
              type="time"
              value={formData.od}
              onChange={(e) => setFormData({ ...formData, od: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Do</label>
            <input
              type="time"
              value={formData.do}
              onChange={(e) => setFormData({ ...formData, do: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Název školy *</label>
            <input
              type="text"
              value={formData.nazev_skoly}
              onChange={(e) => setFormData({ ...formData, nazev_skoly: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">IČO školy *</label>
            <input
              type="text"
              value={formData.ico}
              onChange={(e) => setFormData({ ...formData, ico: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Kontaktní osoba *</label>
            <input
              type="text"
              value={formData.osoba}
              onChange={(e) => setFormData({ ...formData, osoba: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Telefon *</label>
            <input
              type="tel"
              value={formData.telefon}
              onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Ulice *</label>
            <input
              type="text"
              value={formData.ulice}
              onChange={(e) => setFormData({ ...formData, ulice: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Město *</label>
            <input
              type="text"
              value={formData.mesto}
              onChange={(e) => setFormData({ ...formData, mesto: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">PSČ *</label>
            <input
              type="text"
              value={formData.psc}
              onChange={(e) => setFormData({ ...formData, psc: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Přiřazení lektoři</label>
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
              {lectors.map(lector => (
                <label key={lector.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.prirazeni_lektoru.includes(lector.id)}
                    onChange={() => handleMultiSelect('prirazeni_lektoru', lector.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{lector.jmeno}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Kdo prodal *</label>
            <select
              value={formData.kdo_prodal}
              onChange={(e) => setFormData({ ...formData, kdo_prodal: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            >
              {sellers.map(seller => (
                <option key={seller.id} value={seller.id}>{seller.jmeno}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Fakturační firma */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Fakturační firma</label>
          <select
            value={formData.company_id || ''}
            onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">-- Vyberte firmu pro fakturaci --</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">Vyberte firmu pro vytvoření faktury. Každá firma má vlastní API klíč.</p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Počet žáků *</label>
            <input
              type="number"
              value={formData.pocet}
              onChange={(e) => setFormData({ ...formData, pocet: parseInt(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Stav *</label>
            <select
              value={formData.stav}
              onChange={(e) => setFormData({ ...formData, stav: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            >
              {statuses.map(status => (
                <option key={status.id} value={status.id}>{status.nazev}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Poznámka</label>
          <textarea
            value={formData.poznamka}
            onChange={(e) => setFormData({ ...formData, poznamka: e.target.value })}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Workshop položky s počty */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Workshopy a počty</h3>
            <button
              type="button"
              onClick={() => {
                const newItem = { program_id: '', stupen_ids: [], quantity: 1, price_per_unit: 0 };
                setFormData({ 
                  ...formData, 
                  workshop_items: [...formData.workshop_items, newItem],
                  total_workshops: formData.total_workshops + 1
                });
              }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded-md text-sm font-medium"
            >
              + Přidat workshop
            </button>
          </div>
          
          {formData.workshop_items.length === 0 ? (
            <div className="text-gray-500 text-sm italic">
              Žádné workshop položky. Použije se základní cena z pole výše.
            </div>
          ) : (
            <div className="space-y-4">
              {formData.workshop_items.map((item, index) => (
                <div key={index} className="bg-white p-4 rounded-md border border-gray-200">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-gray-900">Workshop #{index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = formData.workshop_items.filter((_, i) => i !== index);
                        const totalWorkshops = Math.max(1, formData.total_workshops - item.quantity);
                        setFormData({ 
                          ...formData, 
                          workshop_items: newItems,
                          total_workshops: totalWorkshops
                        });
                      }}
                      className="text-red-600 hover:text-red-900 p-1"
                      title="Odebrat workshop"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Program *</label>
                      <select
                        value={item.program_id}
                        onChange={(e) => {
                          const newItems = [...formData.workshop_items];
                          newItems[index].program_id = e.target.value;
                          setFormData({ ...formData, workshop_items: newItems });
                        }}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        required
                      >
                        <option value="">Vyberte program</option>
                        {programs.map(program => (
                          <option key={program.id} value={program.id}>{program.nazev}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Počet *</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...formData.workshop_items];
                          const oldQuantity = newItems[index].quantity;
                          const newQuantity = parseInt(e.target.value) || 0;
                          newItems[index].quantity = newQuantity;
                          
                          // Aktualizuj celkový počet workshopů
                          const totalChange = newQuantity - oldQuantity;
                          setFormData({ 
                            ...formData, 
                            workshop_items: newItems,
                            total_workshops: Math.max(1, formData.total_workshops + totalChange)
                          });
                        }}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        min="1"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cena za ks (Kč) *</label>
                      <input
                        type="number"
                        value={item.price_per_unit}
                        onChange={(e) => {
                          const newItems = [...formData.workshop_items];
                          newItems[index].price_per_unit = parseFloat(e.target.value) || 0;
                          setFormData({ ...formData, workshop_items: newItems });
                        }}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stupně</label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
                      {grades.map(grade => (
                        <label key={grade.id} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={(item.stupen_ids || []).includes(grade.id)}
                            onChange={(e) => {
                              const newItems = [...formData.workshop_items];
                              const currentStupne = newItems[index].stupen_ids || [];
                              if (e.target.checked) {
                                newItems[index].stupen_ids = [...currentStupne, grade.id];
                              } else {
                                newItems[index].stupen_ids = currentStupne.filter(id => id !== grade.id);
                              }
                              setFormData({ ...formData, workshop_items: newItems });
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                          />
                          <span className="text-gray-700">{grade.nazev}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                    <strong>Celkem za tento workshop:</strong> {(item.quantity * item.price_per_unit).toLocaleString()} Kč
                  </div>
                </div>
              ))}
              
              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Celkový počet workshopů:</span>
                  <span className="font-bold">{formData.total_workshops}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Celková cena workshopů:</span>
                  <span className="font-bold">
                    {formData.workshop_items.reduce((total, item) => total + (item.quantity * item.price_per_unit), 0).toLocaleString()} Kč
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Doprava - oddělená sekce */}
        <div className="bg-orange-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Doprava</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Počet km</label>
              <input
                type="number"
                value={formData.travel_info.pocet_km}
                onChange={(e) => {
                  const pocet_km = parseInt(e.target.value) || 0;
                  const cena_za_km = formData.travel_info.cena_za_km;
                  const celkova_cena_dopravy = pocet_km * cena_za_km;
                  setFormData({ 
                    ...formData, 
                    travel_info: {
                      ...formData.travel_info,
                      pocet_km,
                      celkova_cena_dopravy
                    }
                  });
                }}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                min="0"
                placeholder="0"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cena za km (Kč)</label>
              <input
                type="number"
                value={formData.travel_info.cena_za_km}
                onChange={(e) => {
                  const cena_za_km = parseFloat(e.target.value) || 0;
                  const pocet_km = formData.travel_info.pocet_km;
                  const celkova_cena_dopravy = pocet_km * cena_za_km;
                  setFormData({ 
                    ...formData, 
                    travel_info: {
                      ...formData.travel_info,
                      cena_za_km,
                      celkova_cena_dopravy
                    }
                  });
                }}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                min="0"
                step="0.5"
                placeholder="0"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Popis dopravy</label>
              <input
                type="text"
                value={formData.travel_info.popis}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  travel_info: {
                    ...formData.travel_info,
                    popis: e.target.value
                  }
                })}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="např. Přerov - Praha - Přerov"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Celkem doprava</label>
              <div className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-sm font-medium text-gray-900">
                {formData.travel_info.celkova_cena_dopravy.toLocaleString()} Kč
              </div>
            </div>
          </div>
        </div>

        {/* Celkové shrnutí */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Celkové shrnutí</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {formData.total_workshops}
              </div>
              <div className="text-gray-600">Workshopů</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {(
                  formData.workshop_items.reduce((total, item) => total + (item.quantity * item.price_per_unit), 0) +
                  formData.travel_info.celkova_cena_dopravy
                ).toLocaleString()} Kč
              </div>
              <div className="text-gray-600">Celková cena</div>
            </div>
            <div className="text-center">
              <div className="text-lg text-gray-700">
                <div>Workshopy: {formData.workshop_items.reduce((total, item) => total + (item.quantity * item.price_per_unit), 0).toLocaleString()} Kč</div>
                <div>Doprava: {formData.travel_info.celkova_cena_dopravy.toLocaleString()} Kč</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Zrušit
          </button>
          <button
            type="submit"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Uložit změny
          </button>
        </div>
      </form>
    </div>
  );
}

// Workshop Form Component
function WorkshopForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    den: '',
    od: '',
    do: '',
    cely_den: 'NE',
    pocet: 1,
    school_id: '',  // Nové pole pro vazbu na školu
    nazev_skoly: '',
    ico: '',
    osoba: '',
    email: '',
    telefon: '',
    mesto: '',
    ulice: '',
    psc: '',
    okres: '',
    vzdalenost_od_prerova: 0,
    program: [],
    stupen: [],
    stav: '',
    poznamka: '',
    kdo_prodal: '',
    company_id: '',  // Pro výběr fakturační firmy
    prirazeni_lektoru: [],
    gps_latitude: null,
    gps_longitude: null,
    cena: 0,
    pocet_km: 0,
    cena_za_km: 0,
    grouped_with: 'NE',
    workshop_items: [], // Nová pole pro podporu množství workshopů
    total_workshops: 1,
    travel_info: {
      pocet_km: 0,
      cena_za_km: 0,
      popis: '',
      celkova_cena_dopravy: 0
    },
    total_price: 0
  });

  const [lectors, setLectors] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [showSchoolSuggestions, setShowSchoolSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [lectorsRes, programsRes, statusesRes, gradesRes, sellersRes, schoolsRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/lectors`),
        axios.get(`${API_URL}/api/programs`),
        axios.get(`${API_URL}/api/statuses`),
        axios.get(`${API_URL}/api/grades`),
        axios.get(`${API_URL}/api/sellers`),
        axios.get(`${API_URL}/api/schools`),
        axios.get(`${API_URL}/api/companies`)
      ]);

      setLectors(lectorsRes.data);
      setPrograms(programsRes.data);
      setStatuses(statusesRes.data);
      setGrades(gradesRes.data);
      setSellers(sellersRes.data);
      setSchools(schoolsRes.data);
      setCompanies(companiesRes.data);
      
      if (statusesRes.data.length > 0) {
        setFormData(prev => ({ ...prev, stav: statusesRes.data[0].id }));
      }
      if (sellersRes.data.length > 0) {
        setFormData(prev => ({ ...prev, kdo_prodal: sellersRes.data[0].id }));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading reference data:', error);
      setLoading(false);
    }
  };

  const searchSchools = async (query) => {
    if (query.length < 2) {
      setSchoolSuggestions([]);
      setShowSchoolSuggestions(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/schools/search?q=${encodeURIComponent(query)}`);
      setSchoolSuggestions(response.data.schools || []);
      setShowSchoolSuggestions(true);
    } catch (error) {
      console.error('Error searching schools:', error);
      setSchoolSuggestions([]);
    }
  };

  const selectSchool = (school) => {
    setFormData({
      ...formData,
      school_id: school.id,
      nazev_skoly: school.nazev_skoly,
      ico: school.ico,
      osoba: school.osoba,
      email: school.email,
      telefon: school.telefon,
      mesto: school.mesto,
      ulice: school.ulice,
      psc: school.psc,
      okres: school.okres,
      gps_latitude: school.gps_latitude,
      gps_longitude: school.gps_longitude
    });
    setShowSchoolSuggestions(false);
    setSchoolSuggestions([]);
  };

  const clearSchoolSelection = () => {
    setFormData({
      ...formData,
      school_id: '',
      nazev_skoly: '',
      ico: '',
      osoba: '',
      email: '',
      telefon: '',
      mesto: '',
      ulice: '',
      psc: '',
      okres: '',
      gps_latitude: null,
      gps_longitude: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/workshops`, formData);
      alert('Workshop byl úspěšně vytvořen!');
      navigate('/dashboard');
    } catch (error) {
      alert('Chyba při vytváření workshopu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  if (loading) {
    return <div className="text-center py-10">Načítání...</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Nový workshop
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        {/* Datum a čas */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Den *</label>
            <input
              type="date"
              data-testid="den-input"
              value={formData.den}
              onChange={(e) => setFormData({ ...formData, den: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Od</label>
            <input
              type="time"
              data-testid="od-input"
              value={formData.od}
              onChange={(e) => setFormData({ ...formData, od: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Do</label>
            <input
              type="time"
              data-testid="do-input"
              value={formData.do}
              onChange={(e) => setFormData({ ...formData, do: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Škola s našeptávačem */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Výběr školy</h3>
            {formData.school_id && (
              <button
                type="button"
                onClick={clearSchoolSelection}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Vymazat výběr školy
              </button>
            )}
          </div>
          
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vyhledat školu (nebo zadejte nové údaje)
            </label>
            <input
              type="text"
              placeholder="Začněte psát název školy, město nebo IČO..."
              value={formData.nazev_skoly}
              onChange={(e) => {
                setFormData({ ...formData, nazev_skoly: e.target.value });
                searchSchools(e.target.value);
              }}
              onFocus={(e) => {
                if (e.target.value.length >= 2) {
                  searchSchools(e.target.value);
                }
              }}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
            
            {/* Suggestions dropdown */}
            {showSchoolSuggestions && schoolSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {schoolSuggestions.map((school) => (
                  <div
                    key={school.id}
                    onClick={() => selectSchool(school)}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">{school.nazev_skoly}</div>
                    <div className="text-sm text-gray-600">
                      {school.mesto} • IČO: {school.ico || 'neuvedeno'} • Posledně: {school.last_workshop || 'nikdy'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {school.total_workshops} workshopů celkem • {school.osoba} • {school.email}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {formData.school_id && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                <div className="text-sm text-green-800">
                  ✅ Vybrána škola: <strong>{formData.nazev_skoly}</strong> ({formData.mesto})
                  <div className="text-xs text-green-600 mt-1">
                    Kontakt: {formData.osoba} • {formData.email} • {formData.telefon}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Škola a kontakt - detaily */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Název školy *</label>
            <input
              type="text"
              data-testid="nazev-skoly-input"
              value={formData.nazev_skoly}
              onChange={(e) => setFormData({ ...formData, nazev_skoly: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">IČO školy *</label>
            <input
              type="text"
              data-testid="ico-input"
              value={formData.ico}
              onChange={(e) => setFormData({ ...formData, ico: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Kontaktní osoba *</label>
            <input
              type="text"
              data-testid="osoba-input"
              value={formData.osoba}
              onChange={(e) => setFormData({ ...formData, osoba: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email *</label>
            <input
              type="email"
              data-testid="email-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Telefon *</label>
            <input
              type="tel"
              data-testid="telefon-input"
              value={formData.telefon}
              onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
        </div>

        {/* Adresa */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Ulice *</label>
            <input
              type="text"
              data-testid="ulice-input"
              value={formData.ulice}
              onChange={(e) => setFormData({ ...formData, ulice: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Město *</label>
            <input
              type="text"
              data-testid="mesto-input"
              value={formData.mesto}
              onChange={(e) => setFormData({ ...formData, mesto: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">PSČ *</label>
            <input
              type="text"
              data-testid="psc-input"
              value={formData.psc}
              onChange={(e) => setFormData({ ...formData, psc: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
        </div>

        {/* Lektoři a prodejce */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Přiřazení lektoři</label>
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
              {lectors.map(lector => (
                <label key={lector.id} className="flex items-center">
                  <input
                    type="checkbox"
                    data-testid={`lector-${lector.id}`}
                    checked={formData.prirazeni_lektoru.includes(lector.id)}
                    onChange={() => handleMultiSelect('prirazeni_lektoru', lector.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{lector.jmeno}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Kdo prodal *</label>
            <select
              data-testid="seller-select"
              value={formData.kdo_prodal}
              onChange={(e) => setFormData({ ...formData, kdo_prodal: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            >
              {sellers.map(seller => (
                <option key={seller.id} value={seller.id}>{seller.jmeno}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Fakturační firma */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Fakturační firma</label>
          <select
            data-testid="company-select"
            value={formData.company_id}
            onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">-- Vyberte firmu pro fakturaci --</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">Vyberte firmu pro vytvoření faktury. Každá firma má vlastní API klíč.</p>
        </div>

        {/* Ostatní údaje */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Počet žáků *</label>
            <input
              type="number"
              data-testid="pocet-input"
              value={formData.pocet}
              onChange={(e) => setFormData({ ...formData, pocet: parseInt(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Stav *</label>
            <select
              data-testid="status-select"
              value={formData.stav}
              onChange={(e) => setFormData({ ...formData, stav: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            >
              {statuses.map(status => (
                <option key={status.id} value={status.id}>{status.nazev}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Poznámka</label>
          <textarea
            data-testid="poznamka-input"
            value={formData.poznamka}
            onChange={(e) => setFormData({ ...formData, poznamka: e.target.value })}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Workshop položky s počty */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Workshopy a počty</h3>
            <button
              type="button"
              onClick={() => {
                const newItem = { program_id: '', stupen_ids: [], quantity: 1, price_per_unit: 0 };
                setFormData({ 
                  ...formData, 
                  workshop_items: [...formData.workshop_items, newItem],
                  total_workshops: formData.total_workshops + 1
                });
              }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded-md text-sm font-medium"
            >
              + Přidat workshop
            </button>
          </div>
          
          {formData.workshop_items.length === 0 ? (
            <div className="text-gray-500 text-sm italic">
              Žádné workshop položky. Použije se základní cena z pole výše.
            </div>
          ) : (
            <div className="space-y-4">
              {formData.workshop_items.map((item, index) => (
                <div key={index} className="bg-white p-4 rounded-md border border-gray-200">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-gray-900">Workshop #{index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = formData.workshop_items.filter((_, i) => i !== index);
                        const totalWorkshops = Math.max(1, formData.total_workshops - item.quantity);
                        setFormData({ 
                          ...formData, 
                          workshop_items: newItems,
                          total_workshops: totalWorkshops
                        });
                      }}
                      className="text-red-600 hover:text-red-900 p-1"
                      title="Odebrat workshop"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Program *</label>
                      <select
                        value={item.program_id}
                        onChange={(e) => {
                          const newItems = [...formData.workshop_items];
                          newItems[index].program_id = e.target.value;
                          setFormData({ ...formData, workshop_items: newItems });
                        }}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        required
                      >
                        <option value="">Vyberte program</option>
                        {programs.map(program => (
                          <option key={program.id} value={program.id}>{program.nazev}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Počet *</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...formData.workshop_items];
                          const oldQuantity = newItems[index].quantity;
                          const newQuantity = parseInt(e.target.value) || 0;
                          newItems[index].quantity = newQuantity;
                          
                          // Aktualizuj celkový počet workshopů
                          const totalChange = newQuantity - oldQuantity;
                          setFormData({ 
                            ...formData, 
                            workshop_items: newItems,
                            total_workshops: Math.max(1, formData.total_workshops + totalChange)
                          });
                        }}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        min="1"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cena za ks (Kč) *</label>
                      <input
                        type="number"
                        value={item.price_per_unit}
                        onChange={(e) => {
                          const newItems = [...formData.workshop_items];
                          newItems[index].price_per_unit = parseFloat(e.target.value) || 0;
                          setFormData({ ...formData, workshop_items: newItems });
                        }}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stupně</label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50">
                      {grades.map(grade => (
                        <label key={grade.id} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={(item.stupen_ids || []).includes(grade.id)}
                            onChange={(e) => {
                              const newItems = [...formData.workshop_items];
                              const currentStupne = newItems[index].stupen_ids || [];
                              if (e.target.checked) {
                                newItems[index].stupen_ids = [...currentStupne, grade.id];
                              } else {
                                newItems[index].stupen_ids = currentStupne.filter(id => id !== grade.id);
                              }
                              setFormData({ ...formData, workshop_items: newItems });
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                          />
                          <span className="text-gray-700">{grade.nazev}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                    <strong>Celkem za tento workshop:</strong> {(item.quantity * item.price_per_unit).toLocaleString()} Kč
                  </div>
                </div>
              ))}
              
              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Celkový počet workshopů:</span>
                  <span className="font-bold">{formData.total_workshops}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Celková cena workshopů:</span>
                  <span className="font-bold">
                    {formData.workshop_items.reduce((total, item) => total + (item.quantity * item.price_per_unit), 0).toLocaleString()} Kč
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Doprava - oddělená sekce */}
        <div className="bg-orange-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Doprava</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Počet km</label>
              <input
                type="number"
                value={formData.travel_info.pocet_km}
                onChange={(e) => {
                  const pocet_km = parseInt(e.target.value) || 0;
                  const cena_za_km = formData.travel_info.cena_za_km;
                  const celkova_cena_dopravy = pocet_km * cena_za_km;
                  setFormData({ 
                    ...formData, 
                    travel_info: {
                      ...formData.travel_info,
                      pocet_km,
                      celkova_cena_dopravy
                    }
                  });
                }}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                min="0"
                placeholder="0"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cena za km (Kč)</label>
              <input
                type="number"
                value={formData.travel_info.cena_za_km}
                onChange={(e) => {
                  const cena_za_km = parseFloat(e.target.value) || 0;
                  const pocet_km = formData.travel_info.pocet_km;
                  const celkova_cena_dopravy = pocet_km * cena_za_km;
                  setFormData({ 
                    ...formData, 
                    travel_info: {
                      ...formData.travel_info,
                      cena_za_km,
                      celkova_cena_dopravy
                    }
                  });
                }}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                min="0"
                step="0.5"
                placeholder="0"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Popis dopravy</label>
              <input
                type="text"
                value={formData.travel_info.popis}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  travel_info: {
                    ...formData.travel_info,
                    popis: e.target.value
                  }
                })}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="např. Přerov - Praha - Přerov"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Celkem doprava</label>
              <div className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-sm font-medium text-gray-900">
                {formData.travel_info.celkova_cena_dopravy.toLocaleString()} Kč
              </div>
            </div>
          </div>
        </div>

        {/* Celkové shrnutí */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Celkové shrnutí</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {formData.total_workshops}
              </div>
              <div className="text-gray-600">Workshopů</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {(
                  formData.workshop_items.reduce((total, item) => total + (item.quantity * item.price_per_unit), 0) +
                  formData.travel_info.celkova_cena_dopravy
                ).toLocaleString()} Kč
              </div>
              <div className="text-gray-600">Celková cena</div>
            </div>
            <div className="text-center">
              <div className="text-lg text-gray-700">
                <div>Workshopy: {formData.workshop_items.reduce((total, item) => total + (item.quantity * item.price_per_unit), 0).toLocaleString()} Kč</div>
                <div>Doprava: {formData.travel_info.celkova_cena_dopravy.toLocaleString()} Kč</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            data-testid="cancel-button"
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Zrušit
          </button>
          <button
            type="submit"
            data-testid="save-button"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Uložit workshop
          </button>
        </div>
      </form>
    </div>
  );
}

// Custom Calendar Component
function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [lectorColors, setLectorColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    loadEvents();
    loadLectorColors();
  }, [currentDate]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API_URL}/api/calendar/month/${year}/${month}`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLectorColors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/calendar/lectors`);
      setLectorColors(response.data.lectors || []);
    } catch (error) {
      console.error('Error loading lector colors:', error);
    }
  };

  const navigate = useNavigate();

  const monthNames = [
    'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
    'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
  ];

  const dayNames = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return events.filter(event => event.date === dateStr);
  };

  const getEventColor = (event) => {
    // AI školení má speciální fialovou barvu
    if (event.is_ai_training) {
      return {
        backgroundColor: '#9333EA',  // Purple-600
        color: '#FFFFFF'
      };
    }
    
    // If event has assigned lector, use lector's color
    if (event.primary_lector_id && lectorColors.length > 0) {
      const lectorColor = lectorColors.find(lector => lector.id === event.primary_lector_id);
      if (lectorColor) {
        return {
          backgroundColor: lectorColor.color,
          color: lectorColor.text_color
        };
      }
    }
    
    // Default colors based on invoice status
    if (event.has_invoice) {
      return {
        backgroundColor: '#10B981',
        color: '#FFFFFF'
      };
    } else {
      return {
        backgroundColor: '#6B7280',
        color: '#FFFFFF'
      };
    }
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Kalendář workshopů</h1>
        <p className="mt-2 text-sm text-gray-700">
          Přehled všech plánovaných workshopů
        </p>
      </div>

      {/* Calendar Header */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={goToPreviousMonth}
              className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
            >
              ‹
            </button>
            <h2 className="text-lg font-semibold text-gray-900">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button
              onClick={goToNextMonth}
              className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
            >
              ›
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Načítání kalendáře...</div>
            </div>
          ) : (
            <>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-px bg-gray-200">
                {/* Day headers */}
                {dayNames.map(dayName => (
                  <div key={dayName} className="bg-gray-50 p-2 text-center text-xs font-medium text-gray-500">
                    {dayName}
                  </div>
                ))}

                {/* Calendar days */}
                {days.map((day, index) => {
                  const dayEvents = getEventsForDay(day);
                  return (
                    <div
                      key={index}
                      className={`bg-white min-h-20 p-1 ${day ? 'hover:bg-gray-50' : ''}`}
                    >
                      {day && (
                        <>
                          <div className="text-sm font-medium text-gray-900 mb-1">{day}</div>
                          <div className="space-y-1">
                            {dayEvents.slice(0, 3).map((event, eventIndex) => {
                              const eventColor = getEventColor(event);
                              return (
                                <div
                                  key={eventIndex}
                                  onClick={() => setSelectedEvent(event)}
                                  className="text-xs p-1 rounded cursor-pointer truncate"
                                  style={{
                                    backgroundColor: eventColor.backgroundColor,
                                    color: eventColor.color
                                  }}
                                  title={`${event.title} - ${event.lector_names.join(', ') || 'Bez lektora'} - ${event.start_time || 'Celý den'}`}
                                >
                                  {event.title.length > 20 ? event.title.substring(0, 20) + '...' : event.title}
                                </div>
                              );
                            })}
                            {dayEvents.length > 3 && (
                              <div className="text-xs text-gray-500 text-center">
                                +{dayEvents.length - 3} další
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Legenda:</h4>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#9333EA' }}></div>
                    <span className="text-sm text-gray-600">AI Školení</span>
                  </div>
                  {lectorColors.map(lector => (
                    <div key={lector.id} className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded mr-2" 
                        style={{ backgroundColor: lector.color }}
                      ></div>
                      <span className="text-sm text-gray-600">{lector.name} (Workshopy)</span>
                    </div>
                  ))}
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-gray-500 rounded mr-2"></div>
                    <span className="text-sm text-gray-600">Bez lektora</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setSelectedEvent(null)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white" onClick={e => e.stopPropagation()}>
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900">{selectedEvent.title}</h3>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-3 text-sm">
                <div><strong>Datum:</strong> {selectedEvent.date}</div>
                {selectedEvent.start_time && (
                  <div><strong>Čas:</strong> {selectedEvent.start_time} - {selectedEvent.end_time || '?'}</div>
                )}
                <div><strong>Místo:</strong> {selectedEvent.location}</div>
                {!selectedEvent.is_ai_training && (
                  <div><strong>Počet workshopů:</strong> {selectedEvent.total_workshops}</div>
                )}
                {selectedEvent.price !== undefined && selectedEvent.price !== null && (
                  <div><strong>Cena:</strong> {selectedEvent.price.toLocaleString()} Kč</div>
                )}
                <div><strong>Stav:</strong> {selectedEvent.status}</div>
                {selectedEvent.lector_names && selectedEvent.lector_names.length > 0 && (
                  <div>
                    <strong>Lektoři:</strong> 
                    <div className="mt-1 flex flex-wrap gap-2">
                      {selectedEvent.lector_names.map((lectorName, index) => {
                        const lector = selectedEvent.lectors[index];
                        const lectorColor = lectorColors.find(lc => lc.id === lector?.id);
                        return (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: lectorColor?.color || '#6B7280',
                              color: lectorColor?.text_color || '#FFFFFF'
                            }}
                          >
                            {lectorName}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                {selectedEvent.has_invoice && (
                  <div><strong>Faktura:</strong> {selectedEvent.invoice_number}</div>
                )}
                <div><strong>Popis:</strong> {selectedEvent.description}</div>
              </div>

              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={async () => {
                    if (window.confirm(`Opravdu chcete smazat tuto událost: ${selectedEvent.title}?`)) {
                      try {
                        if (selectedEvent.is_ai_training) {
                          await axios.delete(`${API_URL}/api/ai-trainings/${selectedEvent.training_id}`);
                        } else {
                          await axios.delete(`${API_URL}/api/workshops/${selectedEvent.id}`);
                        }
                        setSelectedEvent(null);
                        loadEvents(); // Reload calendar
                        alert('Událost byla úspěšně smazána');
                      } catch (error) {
                        console.error('Error deleting event:', error);
                        alert('Chyba při mazání události: ' + (error.response?.data?.detail || error.message));
                      }
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
                >
                  Smazat
                </button>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      if (selectedEvent.is_ai_training) {
                        navigate(`/ai-trainings/${selectedEvent.training_id}`);
                      } else {
                        navigate(`/workshop/edit/${selectedEvent.id}`);
                      }
                      setSelectedEvent(null);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
                  >
                    {selectedEvent.is_ai_training ? 'Zobrazit detail' : 'Upravit workshop'}
                  </button>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400"
                  >
                    Zavřít
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Settings Component
function Settings() {
  const [activeTab, setActiveTab] = useState('lectors');
  const [lectors, setLectors] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [aiTrainingPrograms, setAiTrainingPrograms] = useState([]);
  const [appSettings, setAppSettings] = useState({ vyfakturuj_api_key: '', vyfakturuj_email: '', invoice_due_days: 14 });
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [newItem, setNewItem] = useState({ 
    jmeno: '', 
    nazev: '', 
    nazev_na_fakture: '', 
    email: '', 
    telefon: '', 
    popis: '', 
    username: '', 
    password: '', 
    role: 'user',
    // School fields
    nazev_skoly: '',
    ico: '',
    osoba: '',
    mesto: '',
    ulice: '',
    psc: '',
    okres: '',
    poznamka: '',
    // Company fields
    vyfakturuj_api_key: '',
    vyfakturuj_email: ''
  });
  const [editingItem, setEditingItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [lectorsRes, programsRes, statusesRes, gradesRes, sellersRes, schoolsRes, usersRes, settingsRes, companiesRes, aiProgramsRes] = await Promise.all([
        axios.get(`${API_URL}/api/lectors`),
        axios.get(`${API_URL}/api/programs`),
        axios.get(`${API_URL}/api/statuses`),
        axios.get(`${API_URL}/api/grades`),
        axios.get(`${API_URL}/api/sellers`),
        axios.get(`${API_URL}/api/schools`),
        axios.get(`${API_URL}/api/users`).catch(() => ({ data: [] })), // May fail if not admin
        axios.get(`${API_URL}/api/settings`).catch(() => ({ data: { vyfakturuj_api_key: '', vyfakturuj_email: '', invoice_due_days: 14 } })),
        axios.get(`${API_URL}/api/companies`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/ai-training-programs`).catch(() => ({ data: [] }))
      ]);

      setLectors(lectorsRes.data);
      setPrograms(programsRes.data);
      setStatuses(statusesRes.data);
      setGrades(gradesRes.data);
      setSellers(sellersRes.data);
      setSchools(schoolsRes.data);
      setUsers(usersRes.data);
      setAppSettings(settingsRes.data);
      setCompanies(companiesRes.data);
      setAiTrainingPrograms(aiProgramsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };
  
  const handleSaveSettings = async () => {
    try {
      await axios.post(`${API_URL}/api/settings`, appSettings);
      alert('Nastavení bylo úspěšně uloženo!');
    } catch (error) {
      alert('Chyba při ukládání nastavení: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAdd = async () => {
    try {
      let endpoint = '';
      let data = {};

      switch (activeTab) {
        case 'lectors':
          endpoint = '/api/lectors';
          data = { 
            jmeno: newItem.jmeno, 
            email: newItem.email, 
            telefon: newItem.telefon,
            password: newItem.password,  // Add password
            role: 'lector'
          };
          break;
        case 'programs':
          endpoint = '/api/programs';
          data = { nazev: newItem.nazev, popis: newItem.popis, nazev_na_fakture: newItem.nazev_na_fakture };
          break;
        case 'statuses':
          endpoint = '/api/statuses';
          data = { nazev: newItem.nazev };
          break;
        case 'grades':
          endpoint = '/api/grades';
          data = { nazev: newItem.nazev };
          break;
        case 'sellers':
          endpoint = '/api/sellers';
          data = { jmeno: newItem.jmeno, email: newItem.email };
          break;
        case 'users':
          endpoint = '/api/users/create';
          data = { username: newItem.username, email: newItem.email, password: newItem.password, role: newItem.role };
          break;
        case 'companies':
          endpoint = '/api/companies';
          data = { 
            nazev: newItem.nazev, 
            ico: newItem.ico, 
            ulice: newItem.ulice,
            mesto: newItem.mesto,
            psc: newItem.psc,
            email: newItem.email, 
            telefon: newItem.telefon,
            vyfakturuj_api_key: newItem.vyfakturuj_api_key,
            vyfakturuj_email: newItem.vyfakturuj_email
          };
          break;
        case 'aiTrainingPrograms':
          endpoint = '/api/ai-training-programs';
          data = { nazev: newItem.nazev, popis: newItem.popis, nazev_na_fakture: newItem.nazev_na_fakture };
          break;
        default:
          return;
      }

      await axios.post(`${API_URL}${endpoint}`, data);
      setNewItem({ 
        jmeno: '', nazev: '', nazev_na_fakture: '', email: '', telefon: '', popis: '', 
        username: '', password: '', role: 'user',
        nazev_skoly: '', ico: '', osoba: '', mesto: '', ulice: '', psc: '', okres: '', poznamka: '',
        vyfakturuj_api_key: '', vyfakturuj_email: ''
      });
      loadData();
      alert('Úspěšně přidáno!');
    } catch (error) {
      alert('Chyba při přidávání: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsEditing(true);
    setNewItem({
      jmeno: item.jmeno || '',
      nazev: item.nazev || '',
      nazev_na_fakture: item.nazev_na_fakture || '',
      email: item.email || '',
      telefon: item.telefon || '',
      popis: item.popis || '',
      username: item.username || '',
      password: '',
      role: item.role || 'user',
      nazev_skoly: item.nazev_skoly || '',
      ico: item.ico || '',
      osoba: item.osoba || '',
      mesto: item.mesto || '',
      ulice: item.ulice || '',
      psc: item.psc || '',
      okres: item.okres || '',
      poznamka: item.poznamka || '',
      vyfakturuj_api_key: item.vyfakturuj_api_key || '',
      vyfakturuj_email: item.vyfakturuj_email || ''
    });
  };

  const handleUpdate = async () => {
    try {
      let endpoint = '';
      let data = {};

      switch (activeTab) {
        case 'lectors':
          endpoint = `/api/lectors/${editingItem.id}`;
          data = { jmeno: newItem.jmeno, email: newItem.email, telefon: newItem.telefon };
          if (newItem.password) {
            data.password = newItem.password;  // Only update if provided
          }
          break;
        case 'programs':
          endpoint = `/api/programs/${editingItem.id}`;
          data = { nazev: newItem.nazev, popis: newItem.popis, nazev_na_fakture: newItem.nazev_na_fakture };
          break;
        case 'statuses':
          endpoint = `/api/statuses/${editingItem.id}`;
          data = { nazev: newItem.nazev };
          break;
        case 'grades':
          endpoint = `/api/grades/${editingItem.id}`;
          data = { nazev: newItem.nazev };
          break;
        case 'sellers':
          endpoint = `/api/sellers/${editingItem.id}`;
          data = { jmeno: newItem.jmeno, email: newItem.email };
          break;
        case 'users':
          endpoint = `/api/users/${editingItem.id}`;
          data = { username: newItem.username, email: newItem.email, role: newItem.role };
          if (newItem.password) {
            data.password = newItem.password;
          }
          break;
        case 'companies':
          endpoint = `/api/companies/${editingItem.id}`;
          data = { 
            nazev: newItem.nazev, 
            ico: newItem.ico, 
            ulice: newItem.ulice,
            mesto: newItem.mesto,
            psc: newItem.psc,
            email: newItem.email, 
            telefon: newItem.telefon,
            vyfakturuj_api_key: newItem.vyfakturuj_api_key,
            vyfakturuj_email: newItem.vyfakturuj_email
          };
          break;
        case 'aiTrainingPrograms':
          endpoint = `/api/ai-training-programs/${editingItem.id}`;
          data = { nazev: newItem.nazev, popis: newItem.popis, nazev_na_fakture: newItem.nazev_na_fakture };
          break;
        default:
          return;
      }

      await axios.put(`${API_URL}${endpoint}`, data);
      setNewItem({ 
        jmeno: '', nazev: '', nazev_na_fakture: '', email: '', telefon: '', popis: '', 
        username: '', password: '', role: 'user',
        nazev_skoly: '', ico: '', osoba: '', mesto: '', ulice: '', psc: '', okres: '', poznamka: '',
        vyfakturuj_api_key: '', vyfakturuj_email: ''
      });
      setIsEditing(false);
      setEditingItem(null);
      loadData();
      alert('Úspěšně aktualizováno!');
    } catch (error) {
      alert('Chyba při aktualizaci: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Opravdu chcete smazat "${item.jmeno || item.nazev || item.username}"?`)) {
      return;
    }

    try {
      let endpoint = '';

      switch (activeTab) {
        case 'lectors':
          endpoint = `/api/lectors/${item.id}`;
          break;
        case 'programs':
          endpoint = `/api/programs/${item.id}`;
          break;
        case 'statuses':
          endpoint = `/api/statuses/${item.id}`;
          break;
        case 'grades':
          endpoint = `/api/grades/${item.id}`;
          break;
        case 'sellers':
          endpoint = `/api/sellers/${item.id}`;
          break;
        case 'users':
          endpoint = `/api/users/${item.id}`;
          break;
        case 'companies':
          endpoint = `/api/companies/${item.id}`;
          break;
        case 'aiTrainingPrograms':
          endpoint = `/api/ai-training-programs/${item.id}`;
          break;
        default:
          return;
      }

      await axios.delete(`${API_URL}${endpoint}`);
      loadData();
      alert('Úspěšně smazáno!');
    } catch (error) {
      alert('Chyba při mazání: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleExportData = async () => {
    try {
      setExportLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/export-data`);
      
      // Create JSON file and download
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `workshop_export_${timestamp}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      alert('✅ Data úspěšně exportována!');
    } catch (error) {
      alert('❌ Chyba při exportu: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportData = async () => {
    if (!importFile) {
      alert('Vyberte prosím JSON soubor k importu');
      return;
    }

    if (!window.confirm('⚠️ POZOR: Import přepíše všechna existující data! Opravdu chcete pokračovat?')) {
      return;
    }

    try {
      setImportLoading(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API_URL}/api/admin/import-data`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      alert(`✅ Import dokončen!\n\nImportováno ${response.data.total_records} záznamů:\n- Workshopy: ${response.data.imported.workshops || 0}\n- AI školení: ${response.data.imported.ai_trainings || 0}\n- Školy: ${response.data.imported.schools || 0}\n- Firmy: ${response.data.imported.companies || 0}\n- Lektoři: ${response.data.imported.lectors || 0}`);
      setImportFile(null);
      loadData();
    } catch (error) {
      alert('❌ Chyba při importu: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImportLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingItem(null);
    setNewItem({ jmeno: '', nazev: '', nazev_na_fakture: '', email: '', telefon: '', popis: '', username: '', password: '', role: 'user' });
  };

  const renderItems = (items, type) => (
    <div className="mt-4">
      <ul className="divide-y divide-gray-200">
        {items.map(item => (
          <li key={item.id} className="py-3 flex justify-between items-center">
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">{item.jmeno || item.nazev || item.username}</span>
              {item.email && <span className="text-sm text-gray-500 ml-2">({item.email})</span>}
              {item.telefon && <span className="text-sm text-gray-500 ml-2">({item.telefon})</span>}
              {item.popis && <span className="text-sm text-gray-500 block">{item.popis}</span>}
              {item.nazev_na_fakture && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full ml-2">Faktura: {item.nazev_na_fakture}</span>}
              {item.role && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full ml-2">{item.role}</span>}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handleEdit(item)}
                className="text-primary-600 hover:text-primary-900"
                title="Upravit"
              >
                <FiEdit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(item)}
                className="text-red-600 hover:text-red-900"
                title="Smazat"
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Nastavení</h1>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'lectors', name: 'Lektoři' },
              { id: 'programs', name: 'Programy' },
              { id: 'aiTrainingPrograms', name: 'AI Programy' },
              { id: 'statuses', name: 'Stavy' },
              { id: 'grades', name: 'Stupně' },
              { id: 'sellers', name: 'Prodejci' },
              { id: 'companies', name: 'Fakturační společnosti' },
              { id: 'users', name: 'Uživatelé' },
              { id: 'exportImport', name: 'Export/Import' },
              { id: 'invoicing', name: 'Fakturace' },
              { id: 'auditLog', name: 'Audit Log' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="px-6 py-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {isEditing ? 'Upravit' : 'Přidat'} {activeTab === 'lectors' ? 'lektora' : activeTab === 'programs' ? 'program' : activeTab === 'statuses' ? 'stav' : activeTab === 'grades' ? 'stupeň' : activeTab === 'sellers' ? 'prodejce' : 'uživatele'}
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {(activeTab === 'lectors' || activeTab === 'sellers') && (
                <>
                  <input
                    type="text"
                    placeholder="Jméno"
                    data-testid="name-input"
                    value={newItem.jmeno}
                    onChange={(e) => setNewItem({ ...newItem, jmeno: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    data-testid="email-input"
                    value={newItem.email}
                    onChange={(e) => setNewItem({ ...newItem, email: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  {activeTab === 'lectors' && (
                    <>
                      <input
                        type="tel"
                        placeholder="Telefon"
                        data-testid="telefon-input"
                        value={newItem.telefon}
                        onChange={(e) => setNewItem({ ...newItem, telefon: e.target.value })}
                        className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                      <input
                        type="password"
                        placeholder="Heslo (pro přihlášení lektora)"
                        data-testid="password-input"
                        value={newItem.password}
                        onChange={(e) => setNewItem({ ...newItem, password: e.target.value })}
                        className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                      <p className="text-xs text-gray-500 col-span-full">Email a heslo jsou povinné pro přihlášení lektora do systému</p>
                    </>
                  )}
                </>
              )}
              {activeTab === 'programs' && (
                <>
                  <input
                    type="text"
                    placeholder="Název"
                    data-testid="nazev-input"
                    value={newItem.nazev}
                    onChange={(e) => setNewItem({ ...newItem, nazev: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Název na faktuře (např. Workshop kyberbezpečnosti)"
                    data-testid="nazev-na-fakture-input"
                    value={newItem.nazev_na_fakture}
                    onChange={(e) => setNewItem({ ...newItem, nazev_na_fakture: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Popis"
                    data-testid="popis-input"
                    value={newItem.popis}
                    onChange={(e) => setNewItem({ ...newItem, popis: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </>
              )}
              {activeTab === 'aiTrainingPrograms' && (
                <>
                  <input
                    type="text"
                    placeholder="Název"
                    value={newItem.nazev}
                    onChange={(e) => setNewItem({ ...newItem, nazev: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Název na faktuře"
                    value={newItem.nazev_na_fakture}
                    onChange={(e) => setNewItem({ ...newItem, nazev_na_fakture: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Popis"
                    value={newItem.popis}
                    onChange={(e) => setNewItem({ ...newItem, popis: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </>
              )}
              {activeTab === 'companies' && (
                <>
                  <input
                    type="text"
                    placeholder="Název společnosti"
                    value={newItem.nazev}
                    onChange={(e) => setNewItem({ ...newItem, nazev: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="IČO"
                    value={newItem.ico}
                    onChange={(e) => setNewItem({ ...newItem, ico: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Ulice"
                    value={newItem.ulice}
                    onChange={(e) => setNewItem({ ...newItem, ulice: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Město"
                    value={newItem.mesto}
                    onChange={(e) => setNewItem({ ...newItem, mesto: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="PSČ"
                    value={newItem.psc}
                    onChange={(e) => setNewItem({ ...newItem, psc: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newItem.email}
                    onChange={(e) => setNewItem({ ...newItem, email: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="tel"
                    placeholder="Telefon"
                    value={newItem.telefon}
                    onChange={(e) => setNewItem({ ...newItem, telefon: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Vyfakturuj API klíč"
                    value={newItem.vyfakturuj_api_key}
                    onChange={(e) => setNewItem({ ...newItem, vyfakturuj_api_key: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="email"
                    placeholder="Vyfakturuj Email"
                    value={newItem.vyfakturuj_email}
                    onChange={(e) => setNewItem({ ...newItem, vyfakturuj_email: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </>
              )}
              {(activeTab === 'statuses' || activeTab === 'grades') && (
                <input
                  type="text"
                  placeholder="Název"
                  data-testid="nazev-input"
                  value={newItem.nazev}
                  onChange={(e) => setNewItem({ ...newItem, nazev: e.target.value })}
                  className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              )}
              {activeTab === 'users' && (
                <>
                  <input
                    type="text"
                    placeholder="Username"
                    data-testid="username-input"
                    value={newItem.username}
                    onChange={(e) => setNewItem({ ...newItem, username: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    data-testid="email-input"
                    value={newItem.email}
                    onChange={(e) => setNewItem({ ...newItem, email: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="password"
                    placeholder="Heslo"
                    data-testid="password-input"
                    value={newItem.password}
                    onChange={(e) => setNewItem({ ...newItem, password: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <select
                    value={newItem.role}
                    onChange={(e) => setNewItem({ ...newItem, role: e.target.value })}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </>
              )}
              <div className="flex space-x-3">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleUpdate}
                      data-testid="update-button"
                      className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                    >
                      Aktualizovat
                    </button>
                    <button
                      onClick={handleCancel}
                      data-testid="cancel-button"
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Zrušit
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleAdd}
                    data-testid="add-button"
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Přidat
                  </button>
                )}
              </div>
            </div>
          </div>

          {activeTab === 'lectors' && renderItems(lectors, 'lector')}
          {activeTab === 'programs' && renderItems(programs, 'program')}
          {activeTab === 'aiTrainingPrograms' && renderItems(aiTrainingPrograms, 'aiTrainingProgram')}
          {activeTab === 'statuses' && renderItems(statuses, 'status')}
          {activeTab === 'grades' && renderItems(grades, 'grade')}
          {activeTab === 'sellers' && renderItems(sellers, 'seller')}
          {activeTab === 'schools' && renderItems(schools, 'school')}
          {activeTab === 'companies' && renderItems(companies, 'company')}
          {activeTab === 'users' && renderItems(users, 'user')}
          {activeTab === 'exportImport' && (
            <div className="mt-6 space-y-6">
              {/* Export Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
                  <FiDownload className="mr-2" />
                  Export dat
                </h3>
                <p className="text-sm text-blue-800 mb-4">
                  Exportujte všechna data (workshopy, AI školení, školy, firmy, lektory) do JSON souboru. 
                  Tento soubor můžete použít pro zálohu nebo import do produkčního prostředí.
                </p>
                <button
                  onClick={handleExportData}
                  disabled={exportLoading}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exportLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Exportuji...
                    </>
                  ) : (
                    <>
                      <FiDownload className="mr-2 h-5 w-5" />
                      Exportovat všechna data
                    </>
                  )}
                </button>
                <p className="text-xs text-blue-700 mt-3">
                  💡 Export obsahuje: Workshopy, AI školení, Školy, Firmy, Lektory (bez hesel), Statusy, Programy
                </p>
              </div>

              {/* Import Section */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-orange-900 mb-2 flex items-center">
                  <FiUpload className="mr-2" />
                  Import dat
                </h3>
                <p className="text-sm text-orange-800 mb-4">
                  ⚠️ <strong>POZOR:</strong> Import přepíše všechna existující data v databázi! 
                  Doporučujeme nejdříve exportovat současná data jako zálohu.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vyberte JSON soubor
                    </label>
                    <input
                      type="file"
                      accept=".json"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                    />
                    {importFile && (
                      <p className="text-sm text-green-600 mt-2">
                        ✓ Vybraný soubor: {importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleImportData}
                    disabled={importLoading || !importFile}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Importuji...
                      </>
                    ) : (
                      <>
                        <FiUpload className="mr-2 h-5 w-5" />
                        Importovat data
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-orange-700 mt-3">
                  ⚡ Import vytvoří identickou kopii dat z exportovaného souboru
                </p>
              </div>
            </div>
          )}
          {activeTab === 'invoicing' && (
            <div className="mt-4 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Nastavení fakturace (vyfakturuj.cz)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Pro vytváření faktur potřebujete API klíč a email z vašeho účtu na vyfakturuj.cz.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      vyfakturuj.cz Email *
                    </label>
                    <input
                      type="email"
                      data-testid="vyfakturuj-email"
                      value={appSettings.vyfakturuj_email}
                      onChange={(e) => setAppSettings({ ...appSettings, vyfakturuj_email: e.target.value })}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="info@digitalnimost.cz"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email, kterým jste registrováni na vyfakturuj.cz</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      vyfakturuj.cz API klíč *
                    </label>
                    <input
                      type="text"
                      data-testid="vyfakturuj-api-key"
                      value={appSettings.vyfakturuj_api_key}
                      onChange={(e) => setAppSettings({ ...appSettings, vyfakturuj_api_key: e.target.value })}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Zadejte API klíč z vyfakturuj.cz"
                    />
                    <p className="text-xs text-gray-500 mt-1">API klíč získáte v Nastavení → API na vyfakturuj.cz</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Doba splatnosti (dny)
                    </label>
                    <input
                      type="number"
                      data-testid="invoice-due-days"
                      value={appSettings.invoice_due_days}
                      onChange={(e) => setAppSettings({ ...appSettings, invoice_due_days: parseInt(e.target.value) })}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      min="1"
                      max="365"
                    />
                    <p className="text-xs text-gray-500 mt-1">Výchozí doba splatnosti faktury ve dnech (např. 14 dní)</p>
                  </div>
                  
                  <button
                    onClick={handleSaveSettings}
                    data-testid="save-settings-button"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Uložit nastavení
                  </button>
                </div>
              </div>
              
              {/* Webhook info sekce */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-md font-medium text-gray-900 mb-3">Webhook pro označení zaplacených faktur</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Pro automatické označení zaplacených faktur nastavte tento webhook v nastavení vyfakturuj.cz:
                </p>
                <div className="bg-white p-3 rounded border">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webhook URL (zkopírujte do vyfakturuj.cz):
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={`${window.location.origin}/api/webhook/vyfakturuj?id={id}&number={number}&flags={flags}&total={total}&date_paid={date_paid}`}
                      readOnly
                      className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/api/webhook/vyfakturuj?id={id}&number={number}&flags={flags}&total={total}&date_paid={date_paid}`);
                        alert('Webhook URL zkopírováno do schránky!');
                      }}
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                    >
                      Kopírovat
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Nastavte tento URL ve vyfakturuj.cz → Nastavení → Webhook. 
                    <strong>NEMUSÍTE zaškrtávat "Poslat zároveň data metodou POST"</strong> - používá se GET metoda.
                  </p>
                </div>
                
                <div className="mt-3">
                  <a 
                    href="https://podpora.redbit.cz/navod/webhooky/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    📖 Návod jak nastavit webhook ve vyfakturuj.cz
                  </a>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'auditLog' && (
            <div className="mt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Systémový audit log</h3>
              <AuditLogView />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Audit Log Component
function AuditLogView() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/audit-log?page=${page}&limit=50&search=${encodeURIComponent(search)}`)
      .then(res => { setLogs(res.data.logs || []); setTotal(res.data.total || 0); setPages(res.data.pages || 1); })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [page, search]);

  return (
    <div>
      <div className="flex items-center space-x-2 mb-4">
        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
          placeholder="Hledat v logu..." className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm" />
        <button onClick={() => { setSearch(searchInput); setPage(1); }} className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md">Hledat</button>
        {search && <button onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }} className="px-3 py-2 border text-sm rounded-md">Reset</button>}
      </div>
      <p className="text-sm text-gray-500 mb-3">Celkem: {total} záznamů</p>
      {loading ? <p className="text-gray-500">Načítání...</p> : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Kdy</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Kdo</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Akce</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Detail</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Typ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.map((l, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-600">{l.timestamp ? new Date(l.timestamp).toLocaleString('cs-CZ') : '-'}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{l.user}</td>
                  <td className="px-3 py-2 text-gray-700">{l.action}</td>
                  <td className="px-3 py-2 text-gray-500 max-w-xs truncate" title={l.detail}>{l.detail}</td>
                  <td className="px-3 py-2 text-gray-500">{l.entity_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-gray-600">Stránka {page}/{pages}</span>
          <div className="flex space-x-2">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50">Předchozí</button>
            <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50">Další</button>
          </div>
        </div>
      )}
    </div>
  );
}

// AI Training Form Component
function AITrainingForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    school_id: '',
    company_id: '',
    program_id: '',
    seller_id: '',
    stav_id: '',
    cena_za_ucastnika: 0,
    lessons: [],  // změněno z den, od, do, lektor_id
    participants: [],
    travel_info: {
      pocet_km: 0,
      cena_za_km: 0,
      popis: '',
      celkova_cena_dopravy: 0
    },
    total_price: 0,
    poznamka: ''
  });

  const [companies, setCompanies] = useState([]);
  const [aiPrograms, setAiPrograms] = useState([]);
  const [lectors, setLectors] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [showSchoolSuggestions, setShowSchoolSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [companiesRes, aiProgramsRes, lectorsRes, sellersRes, statusesRes, schoolsRes] = await Promise.all([
        axios.get(`${API_URL}/api/companies`),
        axios.get(`${API_URL}/api/ai-training-programs`),
        axios.get(`${API_URL}/api/lectors`),
        axios.get(`${API_URL}/api/sellers`),
        axios.get(`${API_URL}/api/statuses`),
        axios.get(`${API_URL}/api/schools`)
      ]);

      setCompanies(companiesRes.data);
      setAiPrograms(aiProgramsRes.data);
      setLectors(lectorsRes.data);
      setSellers(sellersRes.data);
      setStatuses(statusesRes.data);
      setSchools(schoolsRes.data);

      // Set defaults
      if (companiesRes.data.length > 0) {
        setFormData(prev => ({ ...prev, company_id: companiesRes.data[0].id }));
      }
      if (aiProgramsRes.data.length > 0) {
        setFormData(prev => ({ ...prev, program_id: aiProgramsRes.data[0].id }));
      }
      if (sellersRes.data.length > 0) {
        setFormData(prev => ({ ...prev, seller_id: sellersRes.data[0].id }));
      }
      if (statusesRes.data.length > 0) {
        setFormData(prev => ({ ...prev, stav_id: statusesRes.data[0].id }));
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading reference data:', error);
      alert('Chyba při načítání dat: ' + error.message);
      setLoading(false);
    }
  };

  const addLesson = () => {
    const defaultLector = lectors.length > 0 ? lectors[0].id : '';
    setFormData({
      ...formData,
      lessons: [
        ...formData.lessons,
        {
          den: '',
          od: '',
          do: '',
          lektor_id: defaultLector,
          forma: 'prezenční',
          poznamka: ''
        }
      ]
    });
  };

  const searchSchools = (query) => {
    setSchoolQuery(query);
    
    if (query.length < 2) {
      setSchoolSuggestions([]);
      setShowSchoolSuggestions(false);
      return;
    }

    // Local filtering
    const filtered = schools.filter(school => 
      school.nazev_skoly?.toLowerCase().includes(query.toLowerCase()) ||
      school.mesto?.toLowerCase().includes(query.toLowerCase()) ||
      school.ico?.includes(query)
    ).slice(0, 10);

    setSchoolSuggestions(filtered);
    setShowSchoolSuggestions(true);
  };

  const selectSchool = (school) => {
    setFormData({ ...formData, school_id: school.id });
    setSchoolQuery(school.nazev_skoly);
    setShowSchoolSuggestions(false);
  };

  const clearSchool = () => {
    setFormData({ ...formData, school_id: '' });
    setSchoolQuery('');
    setSchoolSuggestions([]);
    setShowSchoolSuggestions(false);
  };

  const removeLesson = (index) => {
    const newLessons = formData.lessons.filter((_, i) => i !== index);
    setFormData({ ...formData, lessons: newLessons });
  };

  const updateLesson = (index, field, value) => {
    const newLessons = [...formData.lessons];
    newLessons[index][field] = value;
    setFormData({ ...formData, lessons: newLessons });
  };

  const calculateTotalPrice = (participants, pricePerParticipant, travelCost) => {
    const participantsCost = participants.length * pricePerParticipant;
    return participantsCost + travelCost;
  };

  const addParticipant = () => {
    const newParticipants = [
      ...formData.participants,
      {
        jmeno: '',
        prijmeni: '',
        email: '',
        telefon: '',
        datum_narozeni: '',
        school_id: formData.school_id || '',  // Default to training's school
        certificate_issued: false
      }
    ];
    
    const newTotal = calculateTotalPrice(
      newParticipants,
      formData.cena_za_ucastnika,
      formData.travel_info.celkova_cena_dopravy
    );
    
    setFormData({
      ...formData,
      participants: newParticipants,
      total_price: newTotal
    });
  };

  const removeParticipant = (index) => {
    const newParticipants = formData.participants.filter((_, i) => i !== index);
    
    const newTotal = calculateTotalPrice(
      newParticipants,
      formData.cena_za_ucastnika,
      formData.travel_info.celkova_cena_dopravy
    );
    
    setFormData({ 
      ...formData, 
      participants: newParticipants,
      total_price: newTotal
    });
  };

  const updateParticipant = (index, field, value) => {
    const newParticipants = [...formData.participants];
    newParticipants[index][field] = value;
    setFormData({ ...formData, participants: newParticipants });
  };

  const calculateTravelCost = (km, pricePerKm) => {
    return km * pricePerKm;
  };

  const handleTravelInfoChange = (field, value) => {
    const newTravelInfo = { ...formData.travel_info, [field]: value };
    
    if (field === 'pocet_km' || field === 'cena_za_km') {
      const km = field === 'pocet_km' ? parseFloat(value) || 0 : formData.travel_info.pocet_km;
      const pricePerKm = field === 'cena_za_km' ? parseFloat(value) || 0 : formData.travel_info.cena_za_km;
      newTravelInfo.celkova_cena_dopravy = calculateTravelCost(km, pricePerKm);
    }

    const newTotal = calculateTotalPrice(
      formData.participants,
      formData.cena_za_ucastnika,
      newTravelInfo.celkova_cena_dopravy
    );

    setFormData({
      ...formData,
      travel_info: newTravelInfo,
      total_price: newTotal
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company_id) {
      alert('Prosím vyberte fakturační společnost');
      return;
    }

    if (!formData.program_id) {
      alert('Prosím vyberte program školení');
      return;
    }

    if (formData.lessons.length === 0) {
      alert('Prosím přidejte alespoň jednu lekci');
      return;
    }

    // Validate lessons
    for (let i = 0; i < formData.lessons.length; i++) {
      const lesson = formData.lessons[i];
      if (!lesson.den) {
        alert(`Prosím vyplňte datum lekce ${i + 1}`);
        return;
      }
      if (!lesson.lektor_id) {
        alert(`Prosím vyberte lektora pro lekci ${i + 1}`);
        return;
      }
    }

    // Validate participants (if any are added)
    for (let i = 0; i < formData.participants.length; i++) {
      const p = formData.participants[i];
      if (!p.jmeno || !p.prijmeni) {
        alert(`Prosím vyplňte jméno a příjmení účastníka ${i + 1}`);
        return;
      }
    }

    try {
      await axios.post(`${API_URL}/api/ai-trainings`, formData);
      alert('Školení AI bylo úspěšně vytvořeno!');
      navigate('/ai-trainings');
    } catch (error) {
      console.error('Error creating AI training:', error);
      alert('Chyba při vytváření školení: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Nové školení AI</h1>
        <p className="mt-2 text-sm text-gray-700">
          Vytvořte nové školení AI pro pedagogy
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Základní informace */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              Základní informace
            </h3>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Fakturační společnost *</label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Vyberte společnost</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.nazev}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Program školení *</label>
                <select
                  value={formData.program_id}
                  onChange={(e) => setFormData({ ...formData, program_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Vyberte program</option>
                  {aiPrograms.map(program => (
                    <option key={program.id} value={program.id}>
                      {program.nazev}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Prodejce *</label>
                <select
                  value={formData.seller_id}
                  onChange={(e) => setFormData({ ...formData, seller_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Vyberte prodejce</option>
                  {sellers.map(seller => (
                    <option key={seller.id} value={seller.id}>
                      {seller.jmeno}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Stav *</label>
                <select
                  value={formData.stav_id}
                  onChange={(e) => setFormData({ ...formData, stav_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Vyberte stav</option>
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.nazev}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Cena za účastníka (Kč)</label>
                <input
                  type="number"
                  value={formData.cena_za_ucastnika}
                  onChange={(e) => {
                    const newPrice = parseFloat(e.target.value) || 0;
                    const newTotal = calculateTotalPrice(
                      formData.participants,
                      newPrice,
                      formData.travel_info.celkova_cena_dopravy
                    );
                    setFormData({ ...formData, cena_za_ucastnika: newPrice, total_price: newTotal });
                  }}
                  min="0"
                  step="100"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Škola (volitelné)</label>
                <div className="relative mt-1">
                  <input
                    type="text"
                    placeholder="Začněte psát název školy, město nebo IČO..."
                    value={schoolQuery}
                    onChange={(e) => searchSchools(e.target.value)}
                    onFocus={() => {
                      if (schoolQuery.length >= 2) {
                        setShowSchoolSuggestions(true);
                      }
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  {formData.school_id && (
                    <button
                      type="button"
                      onClick={clearSchool}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                  
                  {showSchoolSuggestions && schoolSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      {schoolSuggestions.map((school) => (
                        <div
                          key={school.id}
                          onClick={() => selectSchool(school)}
                          className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{school.nazev_skoly}</span>
                            <span className="text-gray-500 text-xs">
                              {school.mesto}{school.ico ? `, IČO: ${school.ico}` : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Pokud škola není v seznamu, můžete ji přidat v sekci "Školy"
                </p>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700">Poznámka</label>
              <textarea
                value={formData.poznamka}
                onChange={(e) => setFormData({ ...formData, poznamka: e.target.value })}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Lekce */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Lekce ({formData.lessons.length})
              </h3>
              <button
                type="button"
                onClick={addLesson}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <FiPlus className="-ml-0.5 mr-2 h-4 w-4" />
                Přidat lekci
              </button>
            </div>

            {formData.lessons.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">
                Zatím nebyla přidána žádná lekce. Klikněte na "Přidat lekci" pro přidání.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.lessons.map((lesson, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-medium text-gray-900">Lekce {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeLesson(index)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Datum *</label>
                        <input
                          type="date"
                          value={lesson.den}
                          onChange={(e) => updateLesson(index, 'den', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Čas od</label>
                        <input
                          type="time"
                          value={lesson.od}
                          onChange={(e) => updateLesson(index, 'od', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Čas do</label>
                        <input
                          type="time"
                          value={lesson.do}
                          onChange={(e) => updateLesson(index, 'do', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Lektor *</label>
                        <select
                          value={lesson.lektor_id}
                          onChange={(e) => updateLesson(index, 'lektor_id', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        >
                          <option value="">Vyberte lektora</option>
                          {lectors.map(lector => (
                            <option key={lector.id} value={lector.id}>
                              {lector.jmeno}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Forma *</label>
                        <select
                          value={lesson.forma}
                          onChange={(e) => updateLesson(index, 'forma', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        >
                          <option value="prezenční">Prezenční</option>
                          <option value="online">Online</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Poznámka k lekci</label>
                        <input
                          type="text"
                          value={lesson.poznamka}
                          onChange={(e) => updateLesson(index, 'poznamka', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="např. Google Meet link"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Účastníci */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Účastníci ({formData.participants.length})
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Volitelné - můžete přidat účastníky nyní nebo později
                </p>
              </div>
              <button
                type="button"
                onClick={addParticipant}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <FiPlus className="-ml-0.5 mr-2 h-4 w-4" />
                Přidat účastníka
              </button>
            </div>

            {formData.participants.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">
                Zatím nebyl přidán žádný účastník. Můžete je přidat nyní nebo později po vytvoření školení.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.participants.map((participant, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-medium text-gray-900">Účastník {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeParticipant(index)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Jméno *</label>
                        <input
                          type="text"
                          value={participant.jmeno}
                          onChange={(e) => updateParticipant(index, 'jmeno', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Příjmení *</label>
                        <input
                          type="text"
                          value={participant.prijmeni}
                          onChange={(e) => updateParticipant(index, 'prijmeni', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Datum narození</label>
                        <input
                          type="date"
                          value={participant.datum_narozeni}
                          onChange={(e) => updateParticipant(index, 'datum_narozeni', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          value={participant.email}
                          onChange={(e) => updateParticipant(index, 'email', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Telefon</label>
                        <input
                          type="tel"
                          value={participant.telefon}
                          onChange={(e) => updateParticipant(index, 'telefon', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Doprava */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              Doprava
            </h3>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Popis cesty</label>
                <input
                  type="text"
                  value={formData.travel_info.popis}
                  onChange={(e) => handleTravelInfoChange('popis', e.target.value)}
                  placeholder="např. Přerov - Praha - Přerov"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Počet km</label>
                <input
                  type="number"
                  value={formData.travel_info.pocet_km}
                  onChange={(e) => handleTravelInfoChange('pocet_km', e.target.value)}
                  min="0"
                  step="1"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Cena za km (Kč)</label>
                <input
                  type="number"
                  value={formData.travel_info.cena_za_km}
                  onChange={(e) => handleTravelInfoChange('cena_za_km', e.target.value)}
                  min="0"
                  step="0.1"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="mt-4 bg-gray-50 p-4 rounded-md space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">Účastníci ({formData.participants.length} × {formData.cena_za_ucastnika.toLocaleString()} Kč):</span>
                <span className="font-bold text-gray-900">
                  {(formData.participants.length * formData.cena_za_ucastnika).toLocaleString()} Kč
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">Doprava:</span>
                <span className="font-bold text-gray-900">
                  {formData.travel_info.celkova_cena_dopravy.toLocaleString()} Kč
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2">
                <div className="flex justify-between text-lg">
                  <span className="font-bold text-gray-900">Celková cena:</span>
                  <span className="font-bold text-primary-600">
                    {formData.total_price.toLocaleString()} Kč
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/ai-trainings')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Zrušit
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            Vytvořit školení
          </button>
        </div>
      </form>
    </div>
  );
}

// AI Training Edit Form Component
function AITrainingEditForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    school_id: '',
    company_id: '',
    program_id: '',
    seller_id: '',
    stav_id: '',
    cena_za_ucastnika: 0,
    lessons: [],
    participants: [],
    travel_info: {
      pocet_km: 0,
      cena_za_km: 0,
      popis: '',
      celkova_cena_dopravy: 0
    },
    total_price: 0,
    poznamka: ''
  });

  const [companies, setCompanies] = useState([]);
  const [aiPrograms, setAiPrograms] = useState([]);
  const [lectors, setLectors] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [showSchoolSuggestions, setShowSchoolSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [trainingRes, companiesRes, aiProgramsRes, lectorsRes, sellersRes, statusesRes, schoolsRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-trainings/${id}`),
        axios.get(`${API_URL}/api/companies`),
        axios.get(`${API_URL}/api/ai-training-programs`),
        axios.get(`${API_URL}/api/lectors`),
        axios.get(`${API_URL}/api/sellers`),
        axios.get(`${API_URL}/api/statuses`),
        axios.get(`${API_URL}/api/schools`)
      ]);

      setCompanies(companiesRes.data);
      setAiPrograms(aiProgramsRes.data);
      setLectors(lectorsRes.data);
      setSellers(sellersRes.data);
      setStatuses(statusesRes.data);
      setSchools(schoolsRes.data);

      // Load existing training data
      const training = trainingRes.data;
      setFormData({
        school_id: training.school_id || '',
        company_id: training.company_id || '',
        program_id: training.program_id || '',
        seller_id: training.seller_id || '',
        stav_id: training.stav_id || '',
        cena_za_ucastnika: training.cena_za_ucastnika || 0,
        lessons: training.lessons || [],
        participants: training.participants || [],
        travel_info: training.travel_info || {
          pocet_km: 0,
          cena_za_km: 0,
          popis: '',
          celkova_cena_dopravy: 0
        },
        total_price: training.total_price || 0,
        poznamka: training.poznamka || ''
      });

      // Set school query if school exists
      if (training.school_id) {
        const school = schoolsRes.data.find(s => s.id === training.school_id);
        if (school) {
          setSchoolQuery(school.nazev_skoly);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Chyba při načítání dat: ' + error.message);
      setLoading(false);
    }
  };

  const calculateTotalPrice = (participants, pricePerParticipant, travelCost) => {
    const participantsCost = participants.length * pricePerParticipant;
    return participantsCost + travelCost;
  };

  const calculateTravelCost = (km, pricePerKm) => {
    return km * pricePerKm;
  };

  const searchSchools = (query) => {
    setSchoolQuery(query);
    
    if (query.length < 2) {
      setSchoolSuggestions([]);
      setShowSchoolSuggestions(false);
      return;
    }

    const filtered = schools.filter(school => 
      school.nazev_skoly?.toLowerCase().includes(query.toLowerCase()) ||
      school.mesto?.toLowerCase().includes(query.toLowerCase()) ||
      school.ico?.includes(query)
    ).slice(0, 10);

    setSchoolSuggestions(filtered);
    setShowSchoolSuggestions(true);
  };

  const selectSchool = (school) => {
    setFormData({ ...formData, school_id: school.id });
    setSchoolQuery(school.nazev_skoly);
    setShowSchoolSuggestions(false);
  };

  const clearSchool = () => {
    setFormData({ ...formData, school_id: '' });
    setSchoolQuery('');
    setSchoolSuggestions([]);
    setShowSchoolSuggestions(false);
  };

  const addLesson = () => {
    const defaultLector = lectors.length > 0 ? lectors[0].id : '';
    setFormData({
      ...formData,
      lessons: [
        ...formData.lessons,
        {
          den: '',
          od: '',
          do: '',
          lektor_id: defaultLector,
          forma: 'prezenční',
          poznamka: ''
        }
      ]
    });
  };

  const removeLesson = (index) => {
    const newLessons = formData.lessons.filter((_, i) => i !== index);
    setFormData({ ...formData, lessons: newLessons });
  };

  const updateLesson = (index, field, value) => {
    const newLessons = [...formData.lessons];
    newLessons[index][field] = value;
    setFormData({ ...formData, lessons: newLessons });
  };

  const addParticipant = () => {
    const newParticipants = [
      ...formData.participants,
      {
        jmeno: '',
        prijmeni: '',
        email: '',
        telefon: '',
        datum_narozeni: '',
        school_id: formData.school_id || '',  // Default to training's school
        certificate_issued: false
      }
    ];
    
    const newTotal = calculateTotalPrice(
      newParticipants,
      formData.cena_za_ucastnika,
      formData.travel_info.celkova_cena_dopravy
    );
    
    setFormData({
      ...formData,
      participants: newParticipants,
      total_price: newTotal
    });
  };

  const removeParticipant = (index) => {
    const newParticipants = formData.participants.filter((_, i) => i !== index);
    
    const newTotal = calculateTotalPrice(
      newParticipants,
      formData.cena_za_ucastnika,
      formData.travel_info.celkova_cena_dopravy
    );
    
    setFormData({ 
      ...formData, 
      participants: newParticipants,
      total_price: newTotal
    });
  };

  const updateParticipant = (index, field, value) => {
    const newParticipants = [...formData.participants];
    newParticipants[index][field] = value;
    setFormData({ ...formData, participants: newParticipants });
  };

  const handleTravelInfoChange = (field, value) => {
    const newTravelInfo = { ...formData.travel_info, [field]: value };
    
    if (field === 'pocet_km' || field === 'cena_za_km') {
      const km = field === 'pocet_km' ? parseFloat(value) || 0 : formData.travel_info.pocet_km;
      const pricePerKm = field === 'cena_za_km' ? parseFloat(value) || 0 : formData.travel_info.cena_za_km;
      newTravelInfo.celkova_cena_dopravy = calculateTravelCost(km, pricePerKm);
    }

    const newTotal = calculateTotalPrice(
      formData.participants,
      formData.cena_za_ucastnika,
      newTravelInfo.celkova_cena_dopravy
    );

    setFormData({
      ...formData,
      travel_info: newTravelInfo,
      total_price: newTotal
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company_id) {
      alert('Prosím vyberte fakturační společnost');
      return;
    }

    if (!formData.program_id) {
      alert('Prosím vyberte program školení');
      return;
    }

    if (formData.lessons.length === 0) {
      alert('Prosím přidejte alespoň jednu lekci');
      return;
    }

    // Validate lessons
    for (let i = 0; i < formData.lessons.length; i++) {
      const lesson = formData.lessons[i];
      if (!lesson.den) {
        alert(`Prosím vyplňte datum lekce ${i + 1}`);
        return;
      }
      if (!lesson.lektor_id) {
        alert(`Prosím vyberte lektora pro lekci ${i + 1}`);
        return;
      }
    }

    // Validate participants (if any are added)
    for (let i = 0; i < formData.participants.length; i++) {
      const p = formData.participants[i];
      if (!p.jmeno || !p.prijmeni) {
        alert(`Prosím vyplňte jméno a příjmení účastníka ${i + 1}`);
        return;
      }
    }

    try {
      await axios.put(`${API_URL}/api/ai-trainings/${id}`, formData);
      alert('Školení AI bylo úspěšně upraveno!');
      navigate(`/ai-training/${id}`);
    } catch (error) {
      console.error('Error updating AI training:', error);
      alert('Chyba při úpravě školení: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/ai-training/${id}`)}
          className="text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          ← Zpět na detail
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Upravit školení AI</h1>
        <p className="mt-2 text-sm text-gray-700">
          Upravte informace o školení
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Základní informace - stejné jako v create formuláři */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              Základní informace
            </h3>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Fakturační společnost *</label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Vyberte společnost</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.nazev}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Program školení *</label>
                <select
                  value={formData.program_id}
                  onChange={(e) => setFormData({ ...formData, program_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Vyberte program</option>
                  {aiPrograms.map(program => (
                    <option key={program.id} value={program.id}>
                      {program.nazev}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Prodejce *</label>
                <select
                  value={formData.seller_id}
                  onChange={(e) => setFormData({ ...formData, seller_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Vyberte prodejce</option>
                  {sellers.map(seller => (
                    <option key={seller.id} value={seller.id}>
                      {seller.jmeno}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Stav *</label>
                <select
                  value={formData.stav_id}
                  onChange={(e) => setFormData({ ...formData, stav_id: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Vyberte stav</option>
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.nazev}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Cena za účastníka (Kč)</label>
                <input
                  type="number"
                  value={formData.cena_za_ucastnika}
                  onChange={(e) => {
                    const newPrice = parseFloat(e.target.value) || 0;
                    const newTotal = calculateTotalPrice(
                      formData.participants,
                      newPrice,
                      formData.travel_info.celkova_cena_dopravy
                    );
                    setFormData({ ...formData, cena_za_ucastnika: newPrice, total_price: newTotal });
                  }}
                  min="0"
                  step="100"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700">Škola (volitelné)</label>
                <div className="relative mt-1">
                  <input
                    type="text"
                    placeholder="Začněte psát název školy, město nebo IČO..."
                    value={schoolQuery}
                    onChange={(e) => searchSchools(e.target.value)}
                    onFocus={() => {
                      if (schoolQuery.length >= 2) {
                        setShowSchoolSuggestions(true);
                      }
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  {formData.school_id && (
                    <button
                      type="button"
                      onClick={clearSchool}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                  
                  {showSchoolSuggestions && schoolSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      {schoolSuggestions.map((school) => (
                        <div
                          key={school.id}
                          onClick={() => selectSchool(school)}
                          className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{school.nazev_skoly}</span>
                            <span className="text-gray-500 text-xs">
                              {school.mesto}{school.ico ? `, IČO: ${school.ico}` : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Pokud škola není v seznamu, můžete ji přidat v sekci "Školy"
                </p>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700">Poznámka</label>
              <textarea
                value={formData.poznamka}
                onChange={(e) => setFormData({ ...formData, poznamka: e.target.value })}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Lekce - stejné jako create */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Lekce ({formData.lessons.length})
              </h3>
              <button
                type="button"
                onClick={addLesson}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <FiPlus className="-ml-0.5 mr-2 h-4 w-4" />
                Přidat lekci
              </button>
            </div>

            {formData.lessons.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">
                Zatím nebyla přidána žádná lekce. Klikněte na "Přidat lekci" pro přidání.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.lessons.map((lesson, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-medium text-gray-900">Lekce {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeLesson(index)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Datum *</label>
                        <input
                          type="date"
                          value={lesson.den}
                          onChange={(e) => updateLesson(index, 'den', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Čas od</label>
                        <input
                          type="time"
                          value={lesson.od}
                          onChange={(e) => updateLesson(index, 'od', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Čas do</label>
                        <input
                          type="time"
                          value={lesson.do}
                          onChange={(e) => updateLesson(index, 'do', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Lektor *</label>
                        <select
                          value={lesson.lektor_id}
                          onChange={(e) => updateLesson(index, 'lektor_id', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        >
                          <option value="">Vyberte lektora</option>
                          {lectors.map(lector => (
                            <option key={lector.id} value={lector.id}>
                              {lector.jmeno}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Forma *</label>
                        <select
                          value={lesson.forma}
                          onChange={(e) => updateLesson(index, 'forma', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        >
                          <option value="prezenční">Prezenční</option>
                          <option value="online">Online</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Poznámka k lekci</label>
                        <input
                          type="text"
                          value={lesson.poznamka}
                          onChange={(e) => updateLesson(index, 'poznamka', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="např. Google Meet link"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Účastníci - stejné jako create */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Účastníci ({formData.participants.length})
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Volitelné - můžete přidat účastníky nyní nebo později
                </p>
              </div>
              <button
                type="button"
                onClick={addParticipant}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <FiPlus className="-ml-0.5 mr-2 h-4 w-4" />
                Přidat účastníka
              </button>
            </div>

            {formData.participants.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">
                Zatím nebyl přidán žádný účastník. Můžete je přidat nyní nebo později.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.participants.map((participant, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-medium text-gray-900">Účastník {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeParticipant(index)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Jméno *</label>
                        <input
                          type="text"
                          value={participant.jmeno}
                          onChange={(e) => updateParticipant(index, 'jmeno', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Příjmení *</label>
                        <input
                          type="text"
                          value={participant.prijmeni}
                          onChange={(e) => updateParticipant(index, 'prijmeni', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Škola *</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={participant.school_search_query || schools.find(s => s.id === participant.school_id)?.nazev_skoly || ''}
                            onChange={(e) => {
                              const query = e.target.value;
                              updateParticipant(index, 'school_search_query', query);
                              
                              // Show suggestions if query > 2 chars
                              if (query.length >= 2) {
                                const filtered = schools.filter(school => 
                                  school.nazev_skoly?.toLowerCase().includes(query.toLowerCase()) ||
                                  school.mesto?.toLowerCase().includes(query.toLowerCase()) ||
                                  school.ico?.includes(query)
                                ).slice(0, 10);
                                updateParticipant(index, 'school_suggestions', filtered);
                                updateParticipant(index, 'show_suggestions', true);
                              } else {
                                updateParticipant(index, 'school_suggestions', []);
                                updateParticipant(index, 'show_suggestions', false);
                              }
                            }}
                            onFocus={() => {
                              // Show suggestions if there's a query
                              if (participant.school_search_query && participant.school_search_query.length >= 2) {
                                updateParticipant(index, 'show_suggestions', true);
                              }
                            }}
                            onBlur={() => {
                              // Delay to allow clicking on suggestions
                              setTimeout(() => updateParticipant(index, 'show_suggestions', false), 200);
                            }}
                            placeholder="Začněte psát název školy..."
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            required
                          />
                          {participant.show_suggestions && participant.school_suggestions && participant.school_suggestions.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                              {participant.school_suggestions.map((school) => (
                                <div
                                  key={school.id}
                                  onClick={() => {
                                    updateParticipant(index, 'school_id', school.id);
                                    updateParticipant(index, 'school_search_query', school.nazev_skoly);
                                    updateParticipant(index, 'show_suggestions', false);
                                  }}
                                  className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50"
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">{school.nazev_skoly}</span>
                                    <span className="text-gray-500 text-sm">
                                      {school.mesto} {school.ico && `• IČO: ${school.ico}`}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Datum narození</label>
                        <input
                          type="date"
                          value={participant.datum_narozeni}
                          onChange={(e) => updateParticipant(index, 'datum_narozeni', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          value={participant.email}
                          onChange={(e) => updateParticipant(index, 'email', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Telefon</label>
                        <input
                          type="tel"
                          value={participant.telefon}
                          onChange={(e) => updateParticipant(index, 'telefon', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    
                    {participant.certificate_issued && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Certifikát vystaven {participant.certificate_date}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Doprava */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              Doprava
            </h3>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Popis cesty</label>
                <input
                  type="text"
                  value={formData.travel_info.popis}
                  onChange={(e) => handleTravelInfoChange('popis', e.target.value)}
                  placeholder="např. Přerov - Praha - Přerov"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Počet km</label>
                <input
                  type="number"
                  value={formData.travel_info.pocet_km}
                  onChange={(e) => handleTravelInfoChange('pocet_km', e.target.value)}
                  min="0"
                  step="1"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Cena za km (Kč)</label>
                <input
                  type="number"
                  value={formData.travel_info.cena_za_km}
                  onChange={(e) => handleTravelInfoChange('cena_za_km', e.target.value)}
                  min="0"
                  step="0.1"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="mt-4 bg-gray-50 p-4 rounded-md space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">Účastníci ({formData.participants.length} × {formData.cena_za_ucastnika.toLocaleString()} Kč):</span>
                <span className="font-bold text-gray-900">
                  {(formData.participants.length * formData.cena_za_ucastnika).toLocaleString()} Kč
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">Doprava:</span>
                <span className="font-bold text-gray-900">
                  {formData.travel_info.celkova_cena_dopravy.toLocaleString()} Kč
                </span>
              </div>
              <div className="border-t border-gray-200 pt-2">
                <div className="flex justify-between text-lg">
                  <span className="font-bold text-gray-900">Celková cena:</span>
                  <span className="font-bold text-primary-600">
                    {formData.total_price.toLocaleString()} Kč
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(`/ai-training/${id}`)}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Zrušit
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            Uložit změny
          </button>
        </div>
      </form>
    </div>
  );
}

// AI Training Detail Component
function AITrainingDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [training, setTraining] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [schools, setSchools] = useState([]);
  const [lectors, setLectors] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedSchoolForInvoice, setSelectedSchoolForInvoice] = useState('');
  const [participantSchoolSuggestions, setParticipantSchoolSuggestions] = useState([]);
  const [showParticipantSchoolSuggestions, setShowParticipantSchoolSuggestions] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    jmeno: '',
    prijmeni: '',
    email: '',
    telefon: '',
    datum_narozeni: '',
    school_id: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [trainingRes, programsRes, schoolsRes, lectorsRes, statusesRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-trainings/${id}`),
        axios.get(`${API_URL}/api/ai-training-programs`),
        axios.get(`${API_URL}/api/schools`),
        axios.get(`${API_URL}/api/lectors`),
        axios.get(`${API_URL}/api/statuses`),
        axios.get(`${API_URL}/api/companies`)
      ]);

      setTraining(trainingRes.data);
      setPrograms(programsRes.data);
      setSchools(schoolsRes.data);
      setLectors(lectorsRes.data);
      setStatuses(statusesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error loading training:', error);
      alert('Chyba při načítání školení');
    } finally {
      setLoading(false);
    }
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.nazev : 'N/A';
  };

  const getSchoolName = (schoolId) => {
    if (!schoolId) return 'N/A';
    const school = schools.find(s => s.id === schoolId);
    return school ? school.nazev_skoly : 'N/A';
  };

  const getLectorName = (lectorId) => {
    const lector = lectors.find(l => l.id === lectorId);
    return lector ? lector.jmeno : 'N/A';
  };

  const getStatusName = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    return status ? status.nazev : 'N/A';
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.nazev : 'N/A';
  };

  const handleAddParticipant = async () => {
    if (!newParticipant.jmeno || !newParticipant.prijmeni || !newParticipant.school_id) {
      alert('Prosím vyplňte jméno, příjmení a vyberte školu');
      return;
    }

    try {
      const updatedTraining = {
        ...training,
        participants: [...training.participants, { ...newParticipant, certificate_issued: false }]
      };

      await axios.put(`${API_URL}/api/ai-trainings/${id}`, updatedTraining);
      
      setTraining(updatedTraining);
      setNewParticipant({ jmeno: '', prijmeni: '', email: '', telefon: '', datum_narozeni: '', school_id: '' });
      setShowAddParticipant(false);
      alert('Účastník byl přidán!');
    } catch (error) {
      console.error('Error adding participant:', error);
      alert('Chyba při přidávání účastníka: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateCertificate = async (participantIndex) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ai-trainings/${id}/participants/${participantIndex}/certificate`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const participant = training.participants[participantIndex];
      link.setAttribute('download', `certifikat_${participant.prijmeni}_${participant.jmeno}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Reload training to get updated certificate status
      await loadData();
      
      alert('Certifikát byl vygenerován a stažen!');
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Chyba při generování certifikátu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateInvoice = async () => {
    if (!selectedSchoolForInvoice) {
      alert('Prosím vyberte školu pro vytvoření faktury.');
      return;
    }

    const selectedSchool = schools.find(s => s.id === selectedSchoolForInvoice);
    const schoolName = selectedSchool?.nazev_skoly || 'Vybraná škola';
    
    if (!window.confirm(`Opravdu chcete vytvořit fakturu pro školu: ${schoolName}?`)) {
      return;
    }

    try {
      // Create invoice request with selected school
      const response = await axios.post(`${API_URL}/api/invoices/ai-training/create`, {
        ai_training_id: id,
        school_id: selectedSchoolForInvoice
      });

      alert(`Faktura byla úspěšně vytvořena pro ${schoolName}! Číslo faktury: ${response.data.invoice_number}`);
      
      // Reload training data
      await loadData();
      
      // Close modal and reset selection
      setShowInvoiceModal(false);
      setSelectedSchoolForInvoice('');
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Chyba při vytváření faktury: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Načítání...</div>
      </div>
    );
  }

  if (!training) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">Školení nebylo nalezeno</p>
        <button
          onClick={() => navigate('/ai-trainings')}
          className="mt-4 text-primary-600 hover:text-primary-900"
        >
          Zpět na seznam
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate('/ai-trainings')}
            className="text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            ← Zpět na seznam
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Detail školení AI</h1>
          <p className="mt-2 text-sm text-gray-700">
            {getProgramName(training.program_id)}
          </p>
        </div>
        <div className="space-x-3">
          <button
            onClick={() => setShowInvoiceModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          >
            <FiFileText className="mr-2 h-4 w-4" />
            Vytvořit fakturu
          </button>
          <button
            onClick={() => navigate(`/ai-training/${id}/edit`)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiEdit className="mr-2 h-4 w-4" />
            Upravit
          </button>
        </div>
      </div>

      {/* Invoices Section - NEW */}
      {training.invoices && training.invoices.length > 0 && (
        <div className="bg-white shadow sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Vystavené faktury ({training.invoices.length})
            </h3>
            <div className="space-y-3">
              {training.invoices.map((invoice, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Faktura č. {invoice.invoice_number || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Pro školu: {getSchoolName(invoice.school_id)} • 
                          Vytvořeno: {invoice.created_at ? new Date(invoice.created_at).toLocaleString('cs-CZ') : 'N/A'}
                        </p>
                      </div>
                      {invoice.paid && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Zaplaceno
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        // TODO: Open invoice in vyfakturuj.cz
                        window.open(`https://app.vyfakturuj.cz/faktury/${invoice.invoice_id}`, '_blank');
                      }}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Otevřít ve vyfakturuj.cz
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Basic Info */}
      <div className="bg-white shadow sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Základní informace
          </h3>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Fakturační společnost</dt>
              <dd className="mt-1 text-sm text-gray-900">{getCompanyName(training.company_id)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Program</dt>
              <dd className="mt-1 text-sm text-gray-900">{getProgramName(training.program_id)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Stav</dt>
              <dd className="mt-1 text-sm text-gray-900">{getStatusName(training.stav_id)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Škola</dt>
              <dd className="mt-1 text-sm text-gray-900">{getSchoolName(training.school_id)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Cena za účastníka</dt>
              <dd className="mt-1 text-sm text-gray-900">{training.cena_za_ucastnika?.toLocaleString() || 0} Kč</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Počet účastníků</dt>
              <dd className="mt-1 text-sm text-gray-900">{training.participants?.length || 0}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Celková cena</dt>
              <dd className="mt-1 text-sm font-bold text-primary-600">
                {training.total_price?.toLocaleString() || 0} Kč
              </dd>
            </div>
          </dl>
          {training.poznamka && (
            <div className="mt-4">
              <dt className="text-sm font-medium text-gray-500">Poznámka</dt>
              <dd className="mt-1 text-sm text-gray-900">{training.poznamka}</dd>
            </div>
          )}
        </div>
      </div>

      {/* Lessons */}
      <div className="bg-white shadow sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Lekce ({training.lessons?.length || 0})
          </h3>
          {!training.lessons || training.lessons.length === 0 ? (
            <p className="text-sm text-gray-500">Žádné lekce</p>
          ) : (
            <div className="space-y-4">
              {training.lessons.map((lesson, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 grid grid-cols-1 gap-4 sm:grid-cols-4">
                      <div>
                        <span className="text-xs font-medium text-gray-500">Lekce {index + 1}</span>
                        <p className="mt-1 text-sm font-medium text-gray-900">{lesson.den}</p>
                        {lesson.od && lesson.do && (
                          <p className="text-xs text-gray-500">{lesson.od} - {lesson.do}</p>
                        )}
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Lektor</span>
                        <p className="mt-1 text-sm text-gray-900">{getLectorName(lesson.lektor_id)}</p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Forma</span>
                        <p className="mt-1 text-sm text-gray-900">
                          {lesson.forma === 'online' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Online
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Prezenční
                            </span>
                          )}
                        </p>
                      </div>
                      {lesson.poznamka && (
                        <div className="sm:col-span-4">
                          <span className="text-xs font-medium text-gray-500">Poznámka</span>
                          <p className="mt-1 text-sm text-gray-900">{lesson.poznamka}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Participants */}
      <div className="bg-white shadow sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Účastníci ({training.participants?.length || 0})
            </h3>
            <button
              onClick={() => setShowAddParticipant(!showAddParticipant)}
              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <FiPlus className="mr-2 h-4 w-4" />
              Přidat účastníka
            </button>
          </div>

          {showAddParticipant && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Nový účastník</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <input
                  type="text"
                  placeholder="Jméno *"
                  value={newParticipant.jmeno}
                  onChange={(e) => setNewParticipant({ ...newParticipant, jmeno: e.target.value })}
                  className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <input
                  type="text"
                  placeholder="Příjmení *"
                  value={newParticipant.prijmeni}
                  onChange={(e) => setNewParticipant({ ...newParticipant, prijmeni: e.target.value })}
                  className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                
                {/* School Autocomplete */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Škola * (začněte psát...)"
                    value={newParticipant.school_search_query || schools.find(s => s.id === newParticipant.school_id)?.nazev_skoly || ''}
                    onChange={(e) => {
                      const query = e.target.value;
                      setNewParticipant({ ...newParticipant, school_search_query: query });
                      
                      if (query.length >= 2) {
                        const filtered = schools.filter(school => 
                          school.nazev_skoly?.toLowerCase().includes(query.toLowerCase()) ||
                          school.mesto?.toLowerCase().includes(query.toLowerCase()) ||
                          school.ico?.includes(query)
                        ).slice(0, 10);
                        setParticipantSchoolSuggestions(filtered);
                        setShowParticipantSchoolSuggestions(true);
                      } else {
                        setParticipantSchoolSuggestions([]);
                        setShowParticipantSchoolSuggestions(false);
                      }
                    }}
                    onFocus={() => {
                      if (newParticipant.school_search_query && newParticipant.school_search_query.length >= 2) {
                        setShowParticipantSchoolSuggestions(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowParticipantSchoolSuggestions(false), 200)}
                    className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 w-full"
                  />
                  {showParticipantSchoolSuggestions && participantSchoolSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      {participantSchoolSuggestions.map((school) => (
                        <div
                          key={school.id}
                          onClick={() => {
                            setNewParticipant({ ...newParticipant, school_id: school.id, school_search_query: school.nazev_skoly });
                            setShowParticipantSchoolSuggestions(false);
                          }}
                          className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-sm">{school.nazev_skoly}</span>
                            <span className="text-gray-500 text-xs">
                              {school.mesto} {school.ico && `• IČO: ${school.ico}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <input
                  type="date"
                  placeholder="Datum narození"
                  value={newParticipant.datum_narozeni}
                  onChange={(e) => setNewParticipant({ ...newParticipant, datum_narozeni: e.target.value })}
                  className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newParticipant.email}
                  onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                  className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <input
                  type="tel"
                  placeholder="Telefon"
                  value={newParticipant.telefon}
                  onChange={(e) => setNewParticipant({ ...newParticipant, telefon: e.target.value })}
                  className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="mt-3 flex justify-end space-x-3">
                <button
                  onClick={() => setShowAddParticipant(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Zrušit
                </button>
                <button
                  onClick={handleAddParticipant}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  Přidat
                </button>
              </div>
            </div>
          )}

          {!training.participants || training.participants.length === 0 ? (
            <p className="text-sm text-gray-500">Zatím nebyli přidáni žádní účastníci</p>
          ) : (
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Jméno</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Datum narození</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Telefon</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Certifikát</th>
                    <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Akce</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {training.participants.map((participant, index) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {participant.jmeno} {participant.prijmeni}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {participant.datum_narozeni || '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {participant.email || '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {participant.telefon || '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {participant.certificate_issued ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Vystaven {participant.certificate_date}
                          </span>
                        ) : (
                          <span className="text-gray-400">Nevystaven</span>
                        )}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <button
                          onClick={() => handleGenerateCertificate(index)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          {participant.certificate_issued ? 'Stáhnout znovu' : 'Vygenerovat certifikát'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Invoice School Selection Modal */}
      {showInvoiceModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowInvoiceModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Vytvořit fakturu pro školu
                </h3>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vyberte školu *
                  </label>
                  <select
                    value={selectedSchoolForInvoice}
                    onChange={(e) => setSelectedSchoolForInvoice(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">-- Vyberte školu --</option>
                    {schools
                      .filter(school => {
                        // Show only schools that have participants in this training
                        const schoolId = school.id;
                        const hasParticipants = training.participants && training.participants.some(p => p.school_id === schoolId);
                        
                        // Check if invoice already exists for this school
                        const hasInvoice = training.invoices && training.invoices.some(inv => inv.school_id === schoolId);
                        
                        return hasParticipants && !hasInvoice;
                      })
                      .map(school => {
                        // Count participants from this school
                        const participantCount = training.participants.filter(p => p.school_id === school.id).length;
                        return (
                          <option key={school.id} value={school.id}>
                            {school.nazev_skoly} ({participantCount} {participantCount === 1 ? 'účastník' : participantCount < 5 ? 'účastníci' : 'účastníků'})
                          </option>
                        );
                      })
                    }
                  </select>
                  {schools.filter(school => {
                    const schoolId = school.id;
                    const hasParticipants = training.participants && training.participants.some(p => p.school_id === schoolId);
                    const hasInvoice = training.invoices && training.invoices.some(inv => inv.school_id === schoolId);
                    return hasParticipants && !hasInvoice;
                  }).length === 0 && (
                    <p className="mt-2 text-sm text-amber-600">
                      ⚠️ Pro všechny školy s účastníky již byla vytvořena faktura.
                    </p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    Faktura bude vytvořena pouze pro účastníky z vybrané školy.
                  </p>
                </div>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleCreateInvoice}
                  disabled={!selectedSchoolForInvoice}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Vytvořit fakturu
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setSelectedSchoolForInvoice('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// AI Trainings Management Component
function AITrainingsManagement() {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [schools, setSchools] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [trainingsRes, programsRes, schoolsRes, statusesRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-trainings`),
        axios.get(`${API_URL}/api/ai-training-programs`),
        axios.get(`${API_URL}/api/schools`),
        axios.get(`${API_URL}/api/statuses`)
      ]);
      
      setTrainings(trainingsRes.data);
      setPrograms(programsRes.data);
      setSchools(schoolsRes.data);
      setStatuses(statusesRes.data);
    } catch (error) {
      console.error('Error loading AI trainings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.nazev : programId;
  };

  const getSchoolName = (schoolId) => {
    if (!schoolId) return 'N/A';
    const school = schools.find(s => s.id === schoolId);
    return school ? school.nazev_skoly : 'N/A';
  };

  const getStatusName = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    return status ? status.nazev : statusId;
  };

  const getFirstLessonDate = (training) => {
    if (!training.lessons || training.lessons.length === 0) return 'N/A';
    return training.lessons[0].den;
  };

  const handleDelete = async (trainingId) => {
    if (!window.confirm('Opravdu chcete smazat toto školení AI? Tato akce je nevratná.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/ai-trainings/${trainingId}`);
      alert('Školení bylo úspěšně smazáno');
      loadData();
    } catch (error) {
      console.error('Error deleting training:', error);
      alert('Chyba při mazání školení: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Školení AI</h1>
          <p className="mt-2 text-sm text-gray-700">
            Seznam všech AI školení pro pedagogy
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => navigate('/ai-training/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FiPlus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Přidat školení
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Datum</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Škola</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Program</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Účastníci</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Stav</th>
                    <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Akce</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {trainings.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-10 text-sm text-gray-500">
                        Zatím nebylo vytvořeno žádné školení AI.
                      </td>
                    </tr>
                  ) : (
                    trainings.map((training) => (
                      <tr key={training.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {getFirstLessonDate(training)}
                          {training.lessons && training.lessons.length > 1 && (
                            <span className="ml-2 text-xs text-gray-500">
                              (+{training.lessons.length - 1} další)
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {getSchoolName(training.school_id)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {getProgramName(training.program_id)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {training.participants?.length || 0}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {getStatusName(training.stav_id)}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            onClick={() => navigate(`/ai-training/${training.id}`)}
                            className="text-primary-600 hover:text-primary-900 mr-4"
                          >
                            Detail
                          </button>
                          <button
                            onClick={() => handleDelete(training.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Smazat
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Lector Calendar View Component
function LectorCalendarView({ workshops, lessons, schools, programs, aiPrograms }) {
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [selectedEvent, setSelectedEvent] = React.useState(null);

  const getSchoolName = (schoolId) => {
    const school = schools.find(s => s.id === schoolId);
    return school ? school.nazev_skoly : 'N/A';
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.nazev : 'N/A';
  };

  const getAIProgramName = (programId) => {
    const program = aiPrograms.find(p => p.id === programId);
    return program ? program.nazev : 'N/A';
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, firstDay, lastDay };
  };

  const getEventsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    const workshopEvents = workshops.filter(w => w.den === dateStr).map(w => ({
      type: 'workshop',
      title: getProgramName(w.program?.[0]),
      time: `${w.od} - ${w.do}`,
      school: getSchoolName(w.school_id),
      data: w
    }));

    const lessonEvents = lessons.filter(l => l.lesson.den === dateStr).map(l => ({
      type: 'ai-lesson',
      title: getAIProgramName(l.program_id),
      time: `${l.lesson.od} - ${l.lesson.do}`,
      school: getSchoolName(l.school_id),
      data: l
    }));

    return [...workshopEvents, ...lessonEvents];
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthName = currentDate.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Calendar Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{monthName}</h2>
        <div className="flex space-x-2">
          <button
            onClick={previousMonth}
            className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
          >
            ‹
          </button>
          <button
            onClick={nextMonth}
            className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
          >
            ›
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px mb-2">
          {['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'].map((day, idx) => (
            <div key={idx} className="text-center text-sm font-medium text-gray-700 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {/* Empty cells for days before month starts */}
          {Array.from({ length: (startingDayOfWeek + 6) % 7 }).map((_, idx) => (
            <div key={`empty-${idx}`} className="bg-gray-50 min-h-24"></div>
          ))}

          {/* Days of the month */}
          {Array.from({ length: daysInMonth }).map((_, idx) => {
            const day = idx + 1;
            const events = getEventsForDay(day);
            const isToday = new Date().getDate() === day && 
                           new Date().getMonth() === currentDate.getMonth() &&
                           new Date().getFullYear() === currentDate.getFullYear();

            return (
              <div
                key={day}
                className={`bg-white min-h-24 p-2 ${isToday ? 'ring-2 ring-primary-500' : ''}`}
              >
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary-600 font-bold' : 'text-gray-700'}`}>
                  {day}
                </div>
                <div className="space-y-1">
                  {events.map((event, eventIdx) => (
                    <div
                      key={eventIdx}
                      onClick={() => setSelectedEvent(event)}
                      className={`text-xs p-1 rounded cursor-pointer truncate ${
                        event.type === 'workshop' 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                      }`}
                      title={`${event.time} - ${event.title}`}
                    >
                      <div className="font-semibold">{event.time.split(' - ')[0]}</div>
                      <div className="truncate">{event.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{selectedEvent.title}</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p><strong>Typ:</strong> {selectedEvent.type === 'workshop' ? 'Workshop' : 'AI Lekce'}</p>
              <p><strong>Čas:</strong> {selectedEvent.time}</p>
              <p><strong>Škola:</strong> {selectedEvent.school}</p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedEvent(null)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Lector Dashboard Component
function LectorDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [aiPrograms, setAIPrograms] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'calendar'

  useEffect(() => {
    loadDashboard();
    loadReferenceData();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/lector/dashboard`);
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      alert('Chyba při načítání dashboardu: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
    }
  };

  const loadReferenceData = async () => {
    try {
      const [schoolsRes, programsRes, aiProgramsRes] = await Promise.all([
        axios.get(`${API_URL}/api/schools`),
        axios.get(`${API_URL}/api/programs`),
        axios.get(`${API_URL}/api/ai-training-programs`)
      ]);
      setSchools(schoolsRes.data);
      setPrograms(programsRes.data);
      setAIPrograms(aiProgramsRes.data);
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const getSchoolName = (schoolId) => {
    const school = schools.find(s => s.id === schoolId);
    return school ? school.nazev_skoly : 'N/A';
  };

  const getSchoolDetails = (schoolId) => {
    return schools.find(s => s.id === schoolId);
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.nazev : 'N/A';
  };

  const getAIProgramName = (programId) => {
    const program = aiPrograms.find(p => p.id === programId);
    return program ? program.nazev : 'N/A';
  };

  const getNavigationLink = (school) => {
    if (!school || !school.gps_souradnice) return null;
    const coords = school.gps_souradnice.split(',');
    if (coords.length === 2) {
      return `https://www.google.com/maps/dir/?api=1&destination=${coords[0].trim()},${coords[1].trim()}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Načítání...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div>Chyba při načítání dat</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* STANDALONE HEADER - NO ADMIN NAVIGATION */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-primary-600">Lektorský Portál</h1>
              <div className="ml-8 flex space-x-4">
                <button
                  onClick={() => setView('list')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${view === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  📋 Seznam
                </button>
                <button
                  onClick={() => setView('calendar')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${view === 'calendar' ? 'bg-primary-100 text-primary-700' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  📅 Kalendář
                </button>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <FiLogOut className="mr-2" />
                Odhlásit
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FiCalendar className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Nadcházející</dt>
                    <dd className="text-lg font-semibold text-gray-900">{data.stats.upcoming_count}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FiList className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Workshopy</dt>
                    <dd className="text-lg font-semibold text-gray-900">{data.stats.total_workshops}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FiGrid className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">AI Lekce</dt>
                    <dd className="text-lg font-semibold text-gray-900">{data.stats.total_lessons}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FiFileText className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Dokončené</dt>
                    <dd className="text-lg font-semibold text-gray-900">{data.stats.past_count}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* LIST VIEW */}
        {view === 'list' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Moje Nadcházející Akce</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Program</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Škola</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum & Čas</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontakt</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.upcoming_workshops.map((workshop, idx) => {
                    const school = getSchoolDetails(workshop.school_id);
                    const navLink = getNavigationLink(school);
                    return (
                      <tr key={`w-${idx}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Workshop
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {getProgramName(workshop.program?.[0])}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getSchoolName(workshop.school_id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(workshop.den).toLocaleDateString('cs-CZ')}<br/>
                          {workshop.od} - {workshop.do}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {school?.email && <div>{school.email}</div>}
                          {school?.telefon && <div>{school.telefon}</div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {navLink && (
                            <a href={navLink} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-900 mr-3">
                              🗺️
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {data.upcoming_lessons.map((item, idx) => {
                    const lesson = item.lesson;
                    const school = getSchoolDetails(item.school_id);
                    const navLink = getNavigationLink(school);
                    return (
                      <tr key={`l-${idx}`} className="hover:bg-purple-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            AI Lekce
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {getAIProgramName(item.program_id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getSchoolName(item.school_id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(lesson.den).toLocaleDateString('cs-CZ')}<br/>
                          {lesson.od} - {lesson.do}<br/>
                          <span className="text-xs text-gray-400">{lesson.forma}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {school?.email && <div>{school.email}</div>}
                          {school?.telefon && <div>{school.telefon}</div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {navLink && lesson.forma !== 'Online' && (
                            <a href={navLink} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-900 mr-3">
                              🗺️
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {data.upcoming_workshops.length === 0 && data.upcoming_lessons.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        Nemáte žádné nadcházející akce
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CALENDAR VIEW */}
        {view === 'calendar' && (
          <LectorCalendarView 
            workshops={data.upcoming_workshops} 
            lessons={data.upcoming_lessons}
            schools={schools}
            programs={programs}
            aiPrograms={aiPrograms}
          />
        )}
      </div>
    </div>
  );
}


function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={
            !user ? <Login setUser={setUser} /> : 
            user.role === 'lector' ? <Navigate to="/lector/dashboard" /> :
            user.role === 'admin' ? <Navigate to="/dashboard" /> :
            <DashboardLayout user={user} setUser={setUser}>
              <UserDashboard />
            </DashboardLayout>
          } 
        />
        <Route
          path="/admin-dashboard"
          element={
            user && user.role === 'admin' ? (
              <DashboardLayout user={user} setUser={setUser}>
                <AdminDashboard />
              </DashboardLayout>
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />

        {/* Lector Dashboard Route */}
        <Route
          path="/lector/dashboard"
          element={
            user && user.role === 'lector' ? (
              <LectorDashboard />
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <WorkshopsList />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/workshop/new"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <WorkshopForm />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/workshop/edit/:id"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <WorkshopEditForm />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/calendar"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <Calendar />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/schools"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <SchoolsManagement />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/ai-trainings"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <AITrainingsManagement />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/ai-training/new"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <AITrainingForm />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/ai-training/:id"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <AITrainingDetail />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/ai-training/:id/edit"
          element={
            user && (user.role === 'admin' || user.role === 'user') ? (
              <DashboardLayout user={user} setUser={setUser}>
                <AITrainingEditForm />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/settings"
          element={
            user && user.role === 'admin' ? (
              <DashboardLayout user={user} setUser={setUser}>
                <Settings />
              </DashboardLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
